import { observable, action, computed, runInAction, makeObservable } from 'mobx'
import axios from 'axios'
import api from '../api'
import { selectionStore, uiStore, dataStore } from './UIStore'
import { catchError } from './ErrorStore'
import { mergePopulationDatas } from '../utils'

const hasWorldpopInData = (pd) => {
  if (!pd) return false
  if (Array.isArray(pd)) {
    return pd.some(item => item && (item.ds_name === 'worldpop' || item.dataset === 'worldpop'))
  }
  if (typeof pd === 'object') {
    return Object.values(pd).some(val => {
      if (!val) return false
      if (Array.isArray(val)) {
        return val.some(item => item && (item.ds_name === 'worldpop' || item.dataset === 'worldpop'))
      }
      if (typeof val === 'object') {
        return val.ds_name === 'worldpop' || val.dataset === 'worldpop'
      }
      return false
    })
  }
  return false
}

// Store for managing data for right sidebar (people, income, etc.)
class RightSidebarStore {
  @observable cancelSource = null
  @observable loading = true
  @observable targetGroupId = null
  @observable targetGroupName = null
  @observable originalTargetGroupName = null // Preserve original target group name when showing single polygon
  @observable populationData = null
  @observable populationTrends = []
  @observable maleFemaleData = null
  @observable density = null
  @observable size = null
  @observable areaUnit = 'km²' // 'km²' or 'mi²'
  @observable areaKm = null
  @observable areaMi = null
  @observable totalPopulation = null
  @observable growthRate = null
  @observable years = []
  @observable latestYear = '2024'
  @observable error = null
  @observable polygonData = []
  @observable showPolygonLines = false
  @observable showIndividualPolygonLines = false // When true, show individual lines; when false, show grouped line
  @observable selectedLine = null
  /** Year-keyed population_datas from GET geodata/geopolygons/{id}/ when a trend line is selected in individual-shapes view */
  @observable selectedLinePopulationDatas = null
  @observable singlePolygonData = null
  @observable singlePolygonType = null
  @observable singlePolygonId = null
  @observable noData = false
  @observable aggSelector = 'sum' // 'sum' (aggregate), 'avg' (average), 'dissolved'
  /** People tab Population Trends dataset: landscan | worldpop (default landscan) */
  @observable populationDataset = 'landscan'
  /** From worldpop-task polling; when false, WorldPop option is disabled in People tab */
  @observable worldpopAvailable = false
  /** True while polling worldpop-task for the current target group or polygon */
  @observable worldpopLoading = false
  @observable targetGroupWorldpopAvailable = false
  @observable pyramidLoading = false
  _worldpopPollGeneration = 0
  /** Last shape routing for population-data API (geopolygon / admin / zip). */
  @observable lastShapePopulationOptions = { isZipCode: false, isAdminBoundary: false }
  @observable incomeSelectedYear = '2021' // Selected year for income tab
  @observable populationSelectedYear = null // Selected year for population trends
  @observable showIncomePolygonData = false // Show individual polygons in income tab
  @observable selectedIncomePoint = null

  _fetchingTargetGroupId = null
  _fetchingGeometryId = null
  _fetchingDataset = null

  constructor () {
    makeObservable(this)
  }

  @computed get hasData () {
    return this.targetGroupId !== null && this.populationData !== null
  }

  @computed get genderDataForChart () {
    if (!this.maleFemaleData) return []

    return [{
      name: '',
      male: this.maleFemaleData.male ? -Math.abs(this.maleFemaleData.male) : 0,
      female: this.maleFemaleData.female || 0
    }]
  }

  @computed get polygonGenderDataForChart () {
    if (!this.polygonData || this.polygonData.length === 0) return []
    const selectedYearNum = this.getSelectedPopulationYearNumber()

    // Otherwise, show all polygon data
    return this.polygonData.map(polygon => ({
      id: polygon.id,
      name: polygon.name || `Polygon ${polygon.id}`,
      male: (() => {
        if (selectedYearNum && polygon.malePopulationData && polygon.malePopulationData.length > 0) {
          const maleYearData = polygon.malePopulationData.find(item => item.year === selectedYearNum)
          if (maleYearData) return -Math.abs(maleYearData.value || 0)
        }
        return polygon.maleFemaleData && polygon.maleFemaleData.male ? -Math.abs(polygon.maleFemaleData.male) : 0
      })(),
      female: (() => {
        if (selectedYearNum && polygon.femalePopulationData && polygon.femalePopulationData.length > 0) {
          const femaleYearData = polygon.femalePopulationData.find(item => item.year === selectedYearNum)
          if (femaleYearData) return femaleYearData.value || 0
        }
        return (polygon.maleFemaleData && polygon.maleFemaleData.female) || 0
      })()
    }))
  }

  @computed get populationTrendsForChart () {
    // If we have polygonData from multi-select but no cached trends yet,
    // compute a local aggregate without mutating observables (MobX rule).
    if (this.polygonData && this.polygonData.length > 0 && (!this.populationTrends || this.populationTrends.length === 0)) {
      const computedTrends = this.computeAggregateTrendsFromPolygons(this.polygonData)
      return computedTrends.map(trend => ({
        year: trend.year,
        population: this.aggSelector === 'avg'
          ? (trend.average_population || 0)
          : (trend.total_population || trend.population || 0)
      }))
    }

    if (!this.populationTrends || this.populationTrends.length === 0) return []

    return this.populationTrends.map(trend => ({
      year: trend.year,
      population: this.aggSelector === 'avg'
        ? (trend.average_population || 0)
        : (trend.total_population || trend.population || 0)
    }))
  }

  @computed get polygonTrendsForChart () {
    if (!this.polygonData || this.polygonData.length === 0) return []

    // Get all unique years from all polygons
    const allYears = new Set()
    this.polygonData.forEach(polygon => {
      if (polygon.trends) {
        polygon.trends.forEach(trend => {
          if (trend.year) allYears.add(trend.year)
        })
      }
    })

    const sortedYears = Array.from(allYears).sort()

    // Build chart data structure
    const chartData = sortedYears.map(year => {
      const dataPoint = { year }

      this.polygonData.forEach(polygon => {
        const trend = polygon.trends && polygon.trends.find(t => t.year === year)
        const dataKey = `polygon_${polygon.id}`
        dataPoint[dataKey] = trend ? (trend.population || 0) : 0
      })

      // Also include aggregated values if available
      if (this.aggSelector === 'avg') {
        const avgPopulation = this.polygonData.reduce((sum, polygon) => {
          const trend = polygon.trends && polygon.trends.find(t => t.year === year)
          return sum + (trend ? (trend.population || 0) : 0)
        }, 0) / this.polygonData.length
        dataPoint.average_population = avgPopulation
      } else {
        const aggregatePopulation = this.polygonData.reduce((sum, polygon) => {
          const trend = polygon.trends && polygon.trends.find(t => t.year === year)
          return sum + (trend ? (trend.population || 0) : 0)
        }, 0)
        dataPoint.aggregate_population = aggregatePopulation
      }

      return dataPoint
    })

    return chartData
  }

  @computed get incomeAndPopDataForChart () {
    // for some reason these vars were declared inside each if block.
    // hardcoding this to 2021 for income
    // const selectedYear = this.incomeSelectedYear
    const selectedYear = this.populationDataset === 'landscan' ? '2021' : '2024'
    const selectedYearNum = parseInt(selectedYear)
    const popYearNum = this.getSelectedPopulationYearNumber() || 2024

    // If showing individual polygon data (both flags must be true)
    if (this.showIncomePolygonData && this.showIndividualPolygonLines && this.polygonData && this.polygonData.length > 0) {
      return this.polygonData.map((polygon, index) => {
        const polygonColors = [
          '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
          '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
        ]
        const color = polygon.color || polygonColors[index % polygonColors.length]

        // Get income and population for selected year or latest
        const incomeData = polygon.incomeData && polygon.incomeData.find(d => d.year === selectedYearNum)
        const popData = polygon.trends && (polygon.trends.find(t => t.year === popYearNum) || polygon.trends.find(t => t.year === selectedYearNum) || polygon.trends[polygon.trends.length - 1])

        return {
          id: polygon.id,
          name: polygon.name || `Polygon ${polygon.id}`,
          income: incomeData ? incomeData.income : 0,
          population: popData ? (popData.population || 0) : 0,
          color
        }
      })
    }

    // If showing single polygon data
    if (this.singlePolygonData && this.singlePolygonData.stats) {
      const incomeStats = this.singlePolygonData.stats.hh_income
      const popStats = this.singlePolygonData.stats.total_population

      const incomeData = incomeStats && incomeStats.find(d => d.year === selectedYearNum)
      const popData = popStats && (popStats.find(t => String(t.year) === String(popYearNum)) || popStats.find(t => String(t.year) === String(selectedYearNum)))

      return [{
        id: this.singlePolygonId,
        name: this.targetGroupName || 'Polygon',
        income: incomeData ? incomeData.value : 0,
        population: (this.totalPopulation != null && this.totalPopulation > 0) ? this.totalPopulation : (popData ? popData.value : 0),
        color: '#648E62'
      }]
    }

    // If showing single polygon data coming from geopolygon payload
    // (population_datas keyed by year instead of stats arrays).
    if (this.singlePolygonData && this.singlePolygonData.population_datas) {
      const selectedYearStr = selectedYear !== null && selectedYear !== undefined ? selectedYear.toString() : null
      const yearBucket = selectedYearStr && this.singlePolygonData.population_datas[selectedYearStr]
      const ds = this.populationDataset || 'landscan'
      const yearData = Array.isArray(yearBucket) && yearBucket.length > 0 ? (yearBucket.find(d => d.type === ds) || yearBucket[0]) : null
      const population = (this.totalPopulation != null && this.totalPopulation > 0) ? this.totalPopulation : (yearData ? (yearData.sum_pop || yearData.sum || 0) : 0)
      const income = yearData ? (yearData.hh_income || 0) : 0

      return [{
        id: this.singlePolygonId || this.singlePolygonData.id || 0,
        name: this.targetGroupName || this.singlePolygonData.name || 'Polygon',
        income,
        population,
        color: '#648E62'
      }]
    }

    // Check if we have aggregated polygon data (from Ctrl/Cmd multi-select) - aggregated mode
    if (this.polygonData && this.polygonData.length > 0 && this.populationTrends && this.populationTrends.length > 0) {
      const yearData = this.populationTrends.find(t => t.year === popYearNum) || this.populationTrends.find(t => t.year === selectedYearNum)

      if (!yearData) return []

      // Calculate income for selected year from polygonData
      let totalIncome = 0
      this.polygonData.forEach(polygon => {
        const incomeData = polygon.incomeData && polygon.incomeData.find(d => d.year === selectedYearNum)
        if (incomeData) {
          totalIncome += incomeData.income || 0
        }
      })
      const avgIncome = totalIncome / this.polygonData.length

      return [{
        id: 0,
        name: this.targetGroupName || 'Custom Selection',
        income: this.aggSelector === 'avg' ? avgIncome : totalIncome,
        population: (this.totalPopulation != null && this.totalPopulation > 0) ? this.totalPopulation : (yearData ? (yearData.total_population || yearData.population || 0) : 0),
        color: '#648E62'
      }]
    }

    // Default: show target group data (from populationData API response)
    if (!this.populationData || !this.populationData.data || this.populationData.data.length === 0) {
      return []
    }

    const yearData = this.populationData.data.find(d => d.year === selectedYearNum)

    if (!yearData) return []

    let income = 0
    if (this.aggSelector === 'dissolved') {
      const dissolvedData = this.populationData.dissolved_data && this.populationData.dissolved_data.find(d => d.year === selectedYearNum)
      income = dissolvedData ? (dissolvedData.total_income || 0) : 0
    } else if (this.aggSelector === 'avg') {
      income = yearData.average_income || 0
    } else {
      income = yearData.total_income || 0
    }

    return [{
      id: 0,
      name: this.targetGroupName || 'Target Group',
      income,
      population: (this.totalPopulation != null && this.totalPopulation > 0) ? this.totalPopulation : (yearData.total_population || 0),
      color: '#648E62'
    }]
  }

  getTargetGroupRegionsCount (targetGroupId = null) {
    const tgId = targetGroupId || this.targetGroupId

    if (dataStore && dataStore.targetGroups && tgId != null) {
      const tg = dataStore.targetGroups.find(g => String(g.pk) === String(tgId))
      if (tg) {
        const count = (tg.geopolygons?.length || 0) +
                      (tg.boundaries?.length || 0) +
                      (tg.zipCodeBoundaries?.length || 0)
        if (count > 0) return count
      }
    }

    if (selectionStore) {
      const group = (selectionStore.data && selectionStore.data.group) ||
                    (selectionStore.item && selectionStore.item.geopolygons ? selectionStore.item : null)
      if (group && (tgId == null || String(group.pk) === String(tgId))) {
        const count = (group.geopolygons?.length || 0) +
                      (group.boundaries?.length || 0) +
                      (group.zipCodeBoundaries?.length || 0)
        if (count > 0) return count
      }
    }

    if (this.polygonData && this.polygonData.length > 0) {
      return this.polygonData.length
    }

    if (this.rawPolygonData && Array.isArray(this.rawPolygonData) && this.rawPolygonData.length > 0) {
      return this.rawPolygonData.length
    }

    return 0
  }

  @computed get medianHouseholdIncome () {
    const selectedYear = this.populationDataset === 'landscan' ? '2021' : '2024'
    const selectedYearNum = typeof selectedYear === 'string' ? parseInt(selectedYear, 10) : selectedYear

    // 1. Single polygon view
    if (this.singlePolygonData && this.singlePolygonData.stats && this.singlePolygonData.stats.hh_income) {
      const availableIncomeData = this.singlePolygonData.stats.hh_income.filter(x => x.value !== null && x.value > 0)
      const income = this.singlePolygonData.stats.hh_income.filter(x => x.year === selectedYearNum)
      return income.length > 0 ? income[0].value : (availableIncomeData.length > 0 ? availableIncomeData[0].value : 0)
    }

    if (this.singlePolygonData && this.singlePolygonData.population_datas) {
      const selectedYearStr = selectedYearNum.toString()
      const yearBucket = selectedYearStr && this.singlePolygonData.population_datas[selectedYearStr]
      const ds = this.populationDataset || 'landscan'
      const yearData = Array.isArray(yearBucket) && yearBucket.length > 0 ? (yearBucket.find(d => d.type === ds) || yearBucket[0]) : null
      return yearData ? (yearData.hh_income || 0) : 0
    }

    // 2. Custom multi-polygon selection
    if (this.polygonData && this.polygonData.length > 0 && this.populationTrends && this.populationTrends.length > 0) {
      let totalIncome = 0
      this.polygonData.forEach(polygon => {
        const incomeData = polygon.incomeData && polygon.incomeData.find(d => d.year === selectedYearNum)
        if (incomeData) {
          totalIncome += incomeData.income || 0
        }
      })
      const count = this.polygonData.length
      return count > 0 ? Math.round(totalIncome / count) : 0
    }

    // 3. Target Group view
    if (!this.populationData || !this.populationData.data || this.populationData.data.length === 0) {
      return 0
    }

    const yearData = this.populationData.data.find(d => d.year === selectedYearNum)
    if (!yearData) return 0

    let income = 0
    if (this.aggSelector === 'dissolved') {
      const dissolvedData = this.populationData.dissolved_data && this.populationData.dissolved_data.find(d => d.year === selectedYearNum)
      income = dissolvedData ? (dissolvedData.total_income || 0) : 0
    } else if (this.aggSelector === 'avg') {
      income = yearData.average_income || 0
    } else {
      income = yearData.total_income || 0
    }

    const regionCount = this.getTargetGroupRegionsCount(this.targetGroupId)
    if (regionCount > 0) {
      return Math.round(income / regionCount)
    }

    return income
  }

  @computed get incomeProgressData () {
    // const selectedYear = this.incomeSelectedYear || this.latestYear
    const selectedYear = this.populationDataset === 'landscan' ? '2021' : '2024'
    const selectedYearNum = typeof selectedYear === 'string' ? parseInt(selectedYear, 10) : selectedYear
    // Single polygon view: derive income row from singlePolygonData directly.
    // Do not depend on target-group populationData here, otherwise single-shape
    // responses from /geometries/?geometry=<id> can show "No data available".
    if (this.singlePolygonData && this.singlePolygonData.stats) {
      const incomeSeries = this.singlePolygonData.stats.hh_income || []
      const incomePoint = incomeSeries.find(d => String(d.year) === String(selectedYearNum))
      const income = incomePoint ? (incomePoint.value || 0) : 0

      return [{
        id: this.singlePolygonId || this.singlePolygonData.id || 0,
        name: this.targetGroupName || this.singlePolygonData.name || 'Polygon',
        income,
        maxIncome: Math.max(income, 1)
      }]
    }

    // Single polygon payload can also come as population_datas by year.
    if (this.singlePolygonData && this.singlePolygonData.population_datas) {
      const selectedYearStr = selectedYearNum.toString()
      const yearBucket = selectedYearStr && this.singlePolygonData.population_datas[selectedYearStr]
      const ds = this.populationDataset || 'landscan'
      const yearData = Array.isArray(yearBucket) && yearBucket.length > 0 ? (yearBucket.find(d => d.type === ds) || yearBucket[0]) : null
      const income = yearData ? (yearData.hh_income || 0) : 0

      return [{
        id: this.singlePolygonId || this.singlePolygonData.id || 0,
        name: this.targetGroupName || this.singlePolygonData.name || 'Polygon',
        income,
        maxIncome: Math.max(income, 1)
      }]
    }

    if (!this.populationData || !this.populationData.data || this.populationData.data.length === 0) {
      return []
    }

    // If showing individual polygon data (both flags must be true)
    if (this.showIncomePolygonData && this.showIndividualPolygonLines && this.polygonData && this.polygonData.length > 0) {
      const polygonColors = [
        '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
        '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
      ]

      const progressItems = this.polygonData.map((polygon, index) => {
        const color = polygon.color || polygonColors[index % polygonColors.length]
        const resolvedName = this.resolveShapeName(polygon, index, this.targetGroupId)

        let income = 0
        if (polygon.incomeData && polygon.incomeData.length > 0) {
          const incomePoint = polygon.incomeData.find(d => String(d.year) === String(selectedYearNum)) || polygon.incomeData[0]
          income = incomePoint ? (incomePoint.income || 0) : 0
        } else {
          const extracted = this.extractPolygonIncomeData(polygon)
          const incomePoint = extracted.find(d => String(d.year) === String(selectedYearNum)) || extracted[0]
          income = incomePoint ? (incomePoint.income || 0) : 0
        }

        return {
          id: polygon.id,
          name: resolvedName,
          income,
          maxIncome: 1,
          color
        }
      })

      const allIncomes = progressItems.map(p => p.income || 0)
      const maxInc = Math.max(...allIncomes, 1)
      progressItems.forEach(p => {
        p.maxIncome = maxInc
      })

      return progressItems
    }

    // Default: show target group data with median HH income (filled progress bar)
    const medianIncome = this.medianHouseholdIncome

    return [{
      id: 0,
      name: this.targetGroupName || 'Target Group',
      income: medianIncome,
      maxIncome: medianIncome
    }]
  }

  @computed get incomeYears () {
    if (!this.populationData || !this.populationData.data || this.populationData.data.length === 0) {
      if (this.years && this.years.length > 0) {
        return this.years.map(year => year.val || year.label || year.id).filter(Boolean)
      }
      return []
    }
    return this.populationData.data.map(d => d.year).filter(Boolean).sort()
  }

  getLatestYearNumber () {
    const year = this.latestYear
    if (typeof year === 'string') return parseInt(year, 10)
    return year || null
  }

  getSelectedPopulationYearNumber () {
    const year = this.populationSelectedYear || this.latestYear
    if (typeof year === 'string') return parseInt(year, 10)
    return year || null
  }

  /** Year-keyed population_datas for the selected individual shape (trend line). */
  getPopulationDatasForSelectedShape () {
    if (this.selectedLine == null) return null
    if (this.selectedLinePopulationDatas) {
      return this.selectedLinePopulationDatas
    }
    if (
      this.singlePolygonData &&
      this.singlePolygonData.population_datas &&
      String(this.singlePolygonId) === String(this.selectedLine)
    ) {
      return this.singlePolygonData.population_datas
    }
    const polygon = this.polygonData && this.polygonData.find(
      p => String(p.id) === String(this.selectedLine)
    )
    return (polygon && polygon.population_datas) || null
  }

  getYearPopulationRecordFromDatas (populationDatas, yearNum) {
    if (!populationDatas || yearNum == null) return null
    const yearStr = String(yearNum)
    const bucket = populationDatas[yearStr] || populationDatas[yearNum]
    if (!Array.isArray(bucket) || bucket.length === 0) return null
    const ds = this.populationDataset || 'landscan'
    return bucket.find(d => d.type === ds) || bucket[0]
  }

  getPreviousYearNumber (years, currentYear) {
    if (!years || years.length === 0 || currentYear == null) return null
    const sorted = [...years].sort((a, b) => a - b)
    const idx = sorted.indexOf(currentYear)
    if (idx <= 0) return null
    return sorted[idx - 1]
  }

  calculateGrowthRate (prevPopulation, currPopulation) {
    const prev = Number(prevPopulation) || 0
    const curr = Number(currPopulation) || 0
    if (prev <= 0) return null
    return (((curr - prev) / prev) * 100).toFixed(1)
  }

  getTargetGroupPopulationForYear (yearData, dissolvedData) {
    if (!yearData) return 0
    if (this.aggSelector === 'dissolved' && dissolvedData) {
      return dissolvedData.total_population || 0
    }
    if (this.aggSelector === 'avg') {
      return yearData.average_population || 0
    }
    return yearData.total_population || 0
  }

  @action updateTotalPopulationForSelectedYear () {
    const selectedYearNum = this.getSelectedPopulationYearNumber()
    if (!selectedYearNum) return

    // Selected individual shape — use that shape's population_datas for the pinned year
    if (this.selectedLine != null && this.showIndividualPolygonLines) {
      const yearData = this.getYearPopulationRecordFromDatas(
        this.getPopulationDatasForSelectedShape(),
        selectedYearNum
      )
      if (yearData) {
        this.totalPopulation = yearData.sum_pop || yearData.sum || 0
        return
      }

      const selectedPolygon = this.polygonData && this.polygonData.find(
        p => String(p.id) === String(this.selectedLine)
      )
      if (selectedPolygon && selectedPolygon.trends && selectedPolygon.trends.length > 0) {
        const selectedTrend = selectedPolygon.trends.find(t => t.year === selectedYearNum)
        if (selectedTrend) {
          this.totalPopulation = selectedTrend.population || 0
          return
        }
      }
    }

    // Single polygon data takes precedence
    if (this.singlePolygonData && this.singlePolygonData.stats && this.singlePolygonData.stats.total_population) {
      const yearData = this.singlePolygonData.stats.total_population.find(d => String(d.year) === String(selectedYearNum))
      if (yearData) {
        this.totalPopulation = yearData.value || 0
        return
      }
    }

    // Single polygon payload can also come from geopolygon endpoint with
    // population_datas keyed by year.
    if (this.singlePolygonData && this.singlePolygonData.population_datas) {
      const yearBucket = this.singlePolygonData.population_datas[selectedYearNum.toString()]
      const ds = this.populationDataset || 'landscan'
      const yearData = Array.isArray(yearBucket) && yearBucket.length > 0 ? (yearBucket.find(d => d.type === ds) || yearBucket[0]) : null
      if (yearData) {
        this.totalPopulation = yearData.sum_pop || yearData.sum || 0
        return
      }
    }

    // Target group data from API
    if (this.populationData && this.populationData.data && this.populationData.data.length > 0) {
      const yearData = this.populationData.data.find(d => d.year === selectedYearNum)
      if (yearData) {
        const dissolvedData = this.populationData.dissolved_data &&
          this.populationData.dissolved_data.find(d => d.year === selectedYearNum)
        this.totalPopulation = this.getTargetGroupPopulationForYear(yearData, dissolvedData)
        return
      }
    }

    // Fallback: aggregated trends from polygons
    if (this.populationTrends && this.populationTrends.length > 0) {
      const yearTrend = this.populationTrends.find(t => t.year === selectedYearNum)
      if (yearTrend) {
        this.totalPopulation = this.aggSelector === 'avg'
          ? (yearTrend.average_population || yearTrend.population || 0)
          : (yearTrend.total_population || yearTrend.population || 0)
      }
    }

    // Final fallback: use latest available year
    if (this.populationData && this.populationData.data && this.populationData.data.length > 0) {
      const latestData = this.populationData.data[this.populationData.data.length - 1]
      this.totalPopulation = this.aggSelector === 'avg'
        ? (latestData.average_population || 0)
        : (latestData.total_population || 0)
    }
  }

  calculateRatioOfAverages (averagePopulation, areaKm, countPolygons) {
    // This function returns the density value to be displayed when the Aggregate section is selected
    // We calculate the ratio of averages (average population / average size)
    if (!areaKm || !countPolygons) return null
    const ratio = (averagePopulation || 0) / (areaKm / countPolygons)
    if (!Number.isFinite(ratio)) return null
    return Number(ratio.toFixed(0))
  }

  calculateAverageOfRatios (polygons, year) {
    if (!polygons || polygons.length === 0 || !year) return null
    let sum = 0
    let count = 0
    polygons.forEach(polygon => {
      const area = polygon.areaSqkm
      if (!area || area <= 0) return
      const trend = polygon.trends && polygon.trends.find(t => t.year === year)
      const population = trend ? (trend.population || 0) : 0
      const ratio = population / area
      if (Number.isFinite(ratio)) {
        sum += ratio
        count += 1
      }
    })
    if (!count) return null
    return Number((sum / count).toFixed(0))
  }

  calculatePolygonDensity (polygon, year) {
    if (!polygon || !year) return null
    const area = polygon.areaSqkm
    if (!area || area <= 0) return null
    const trend = polygon.trends && polygon.trends.find(t => t.year === year)
    const population = trend ? (trend.population || 0) : 0
    const ratio = population / area
    if (!Number.isFinite(ratio)) return null
    return Number(ratio.toFixed(3))
  }

  @action getNewCancelSource () {
    if (this.cancelSource) {
      this.cancelSource.cancel('Cancellation due to user clicking another item')
    }
    this.cancelSource = axios.CancelToken.source()
    return this.cancelSource
  }

  @action setLoading (loading) {
    if (!loading && (this._fetchingTargetGroupId !== null || this._fetchingGeometryId !== null)) {
      return
    }
    this.loading = loading
  }

  @action setTargetGroup (targetGroupId, targetGroupName) {
    this.targetGroupId = targetGroupId
    this.targetGroupName = targetGroupName
    this.resetData()
  }

  mergeStats (existingStats, newStats) {
    if (!existingStats) return newStats
    if (!newStats) return existingStats
    const merged = { ...existingStats }
    const keys = ['total_population', 'female_population', 'male_population', 'density', 'hh_income']
    keys.forEach(key => {
      if (newStats[key] && Array.isArray(newStats[key])) {
        const existingArr = merged[key] || []
        const newArr = newStats[key]

        // Merge without duplicating type+year combinations
        const combined = [...existingArr]
        newArr.forEach(newItem => {
          const exists = combined.findIndex(ex => ex.year === newItem.year && ex.type === newItem.type)
          if (exists >= 0) {
            combined[exists] = newItem
          } else {
            combined.push(newItem)
          }
        })
        merged[key] = combined
      }
    })
    return merged
  }

  mergePolygons (existingPolygons, newPolygons) {
    if (!existingPolygons) return newPolygons
    if (!newPolygons) return existingPolygons
    return existingPolygons.map((ep, i) => {
      const np = newPolygons.find(p => p.id === ep.id)
      if (np) {
        const mergedPopulationDatas = { ...(ep.population_datas || {}) }
        if (np.population_datas) {
          for (const year of Object.keys(np.population_datas)) {
            const newArray = Array.isArray(np.population_datas[year]) ? np.population_datas[year] : [np.population_datas[year]]
            const existingArray = Array.isArray(mergedPopulationDatas[year]) ? mergedPopulationDatas[year] : (mergedPopulationDatas[year] ? [mergedPopulationDatas[year]] : [])
            mergedPopulationDatas[year] = [...existingArray, ...newArray]
          }
        }
        return {
          ...ep,
          stats: this.mergeStats(ep.stats, np.stats),
          population_datas: mergedPopulationDatas
        }
      }
      return ep
    })
  }

  getFilteredStats (stats) {
    if (!stats) return stats
    const ds = this.populationDataset || 'landscan'
    const filterFn = (arr) => {
      if (!Array.isArray(arr)) return arr
      const hasType = arr.some(item => item && item.type)
      if (hasType) {
        return arr.filter(item => item.type === ds)
      }
      return arr
    }

    return {
      ...stats,
      total_population: filterFn(stats.total_population),
      female_population: filterFn(stats.female_population),
      male_population: filterFn(stats.male_population),
      density: filterFn(stats.density),
      hh_income: filterFn(stats.hh_income)
    }
  }

  getFilteredYearlyData (data) {
    if (!Array.isArray(data)) return data
    const ds = this.populationDataset || 'landscan'
    const hasType = data.some(item => item && item.type)
    if (!hasType) return data
    return data.filter(item => item && item.type === ds)
  }

  getFilteredPolygon (polygon) {
    if (!polygon || !polygon.stats) return polygon
    return {
      ...polygon,
      stats: this.getFilteredStats(polygon.stats)
    }
  }

  findTargetGroupShape (polygonRaw, index = null, targetGroupId = null) {
    if (!polygonRaw) return null

    const polyId = polygonRaw.id != null ? String(polygonRaw.id) : null
    const remoteDbId = polygonRaw.remote_db_id != null
      ? String(polygonRaw.remote_db_id)
      : (polygonRaw.admin_boundary_id != null
          ? String(polygonRaw.admin_boundary_id)
          : (polygonRaw.admin_boundary != null
              ? String(polygonRaw.admin_boundary)
              : (polygonRaw.zip_boundary_id != null
                  ? String(polygonRaw.zip_boundary_id)
                  : (polygonRaw.zip_boundary != null ? String(polygonRaw.zip_boundary) : null))))
    const innerId = polygonRaw.inner_id != null ? String(polygonRaw.inner_id) : null

    try {
      const tgId = targetGroupId || this.targetGroupId
      const targetGroupsToSearch = []
      if (dataStore && dataStore.targetGroups) {
        if (tgId) {
          const primaryTg = dataStore.targetGroups.find(g => String(g.pk) === String(tgId))
          if (primaryTg) targetGroupsToSearch.push(primaryTg)
        }
        dataStore.targetGroups.forEach(tg => {
          if (!targetGroupsToSearch.includes(tg)) {
            targetGroupsToSearch.push(tg)
          }
        })
      }

      for (const tg of targetGroupsToSearch) {
        // 1. Check boundaries (Admin Boundaries)
        if (tg.boundaries && tg.boundaries.length > 0) {
          const matched = tg.boundaries.find(b => {
            const bId = String(b.id || '')
            const bRemote = String(b.remote_db_id || '')
            const bInner = String(b.inner_id || '')
            return (
              (polyId && (bId === polyId || bRemote === polyId || bInner === polyId)) ||
              (remoteDbId && (bId === remoteDbId || bRemote === remoteDbId || bInner === remoteDbId)) ||
              (innerId && (bId === innerId || bRemote === innerId || bInner === innerId))
            )
          })
          if (matched) return matched
        }

        // 2. Check zipCodeBoundaries
        if (tg.zipCodeBoundaries && tg.zipCodeBoundaries.length > 0) {
          const matched = tg.zipCodeBoundaries.find(b => {
            const bId = String(b.id || '')
            const bRemote = String(b.remote_db_id || '')
            const bInner = String(b.inner_id || '')
            return (
              (polyId && (bId === polyId || bRemote === polyId || bInner === polyId)) ||
              (remoteDbId && (bId === remoteDbId || bRemote === remoteDbId || bInner === remoteDbId)) ||
              (innerId && (bId === innerId || bRemote === innerId || bInner === innerId))
            )
          })
          if (matched) return matched
        }

        // 3. Check geopolygons
        if (tg.geopolygons && tg.geopolygons.length > 0) {
          const matched = tg.geopolygons.find(g => {
            const gId = String(g.id || '')
            return polyId && gId === polyId
          })
          if (matched) return matched
        }

        // 4. Check all regions
        if (tg.regions && tg.regions.length > 0) {
          const matched = tg.regions.find(r => {
            const rId = String(r.id || '')
            const rRemote = String(r.remote_db_id || '')
            const rInner = String(r.inner_id || '')
            return (
              (polyId && (rId === polyId || rRemote === polyId || rInner === polyId)) ||
              (remoteDbId && (rId === remoteDbId || rRemote === remoteDbId || rInner === remoteDbId)) ||
              (innerId && (rId === innerId || rRemote === innerId || rInner === innerId))
            )
          })
          if (matched) return matched
        }

        // 5. Index-based match fallback
        if (index != null && tg.regions && tg.regions.length > index) {
          const r = tg.regions[index]
          if (r) return r
        }
      }
    } catch (e) {
      console.warn('Error finding target group shape:', e)
    }

    return null
  }

  resolveShapeName (polygonRaw, index = null, targetGroupId = null) {
    if (!polygonRaw) return `Polygon ${index != null ? index + 1 : ''}`

    const isGenericOrIdOrNA = (str) => {
      if (!str) return true
      const s = String(str).trim()
      if (!s) return true
      if (s.toLowerCase() === 'n/a' || s.toLowerCase() === 'na' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return true
      if (/^\d+$/.test(s)) return true // Pure numeric string / ID
      if (/^Polygon\s*\d*$/i.test(s)) return true // "Polygon 123" or "Polygon"
      if (/^Admin\s*Boundary\s*\d*$/i.test(s)) return true
      return false
    }

    // 1. Try resolving from target group boundary / geopolygon
    const matchedShape = this.findTargetGroupShape(polygonRaw, index, targetGroupId)
    if (matchedShape && matchedShape.name && !isGenericOrIdOrNA(matchedShape.name)) {
      return matchedShape.name
    }

    // 2. Direct candidate names on polygonRaw
    const candidateName = polygonRaw.name || polygonRaw.admin_name || polygonRaw.boundary_name || polygonRaw.inner_name || polygonRaw.title || polygonRaw.label
    if (candidateName && !isGenericOrIdOrNA(candidateName)) {
      return candidateName
    }

    // 3. Fallback
    if (matchedShape && matchedShape.name) {
      return matchedShape.name
    }
    if (candidateName && candidateName.toLowerCase() !== 'n/a' && candidateName.toLowerCase() !== 'null') {
      return candidateName
    }
    return `Polygon ${index != null ? index + 1 : (polygonRaw.id || '')}`
  }

  extractPolygonIncomeData (polygonRaw, matchedShape = null) {
    let incomeData = []

    // 1. Check polygon.stats.hh_income
    if (polygonRaw && polygonRaw.stats && polygonRaw.stats.hh_income && Array.isArray(polygonRaw.stats.hh_income) && polygonRaw.stats.hh_income.length > 0) {
      incomeData = polygonRaw.stats.hh_income.map(item => ({
        year: item.year != null ? (typeof item.year === 'string' ? parseInt(item.year, 10) : item.year) : 2021,
        income: item.value != null && !isNaN(item.value) ? Number(item.value) : 0
      }))
      if (incomeData.some(d => d.income > 0)) {
        return incomeData
      }
    }

    // 2. Check population_datas on polygonRaw or matchedShape
    const popDatas = (polygonRaw && polygonRaw.population_datas) || (matchedShape && matchedShape.population_datas)
    if (popDatas) {
      const ds = this.populationDataset || 'landscan'
      if (Array.isArray(popDatas)) {
        incomeData = popDatas.map(item => ({
          year: item.year != null ? (typeof item.year === 'string' ? parseInt(item.year, 10) : item.year) : 2021,
          income: item.hh_income != null ? Number(item.hh_income) : (item.income != null ? Number(item.income) : 0)
        }))
      } else if (typeof popDatas === 'object') {
        incomeData = Object.entries(popDatas).map(([yearStr, yearBucket]) => {
          const yr = parseInt(yearStr, 10) || 2021
          let inc = 0
          if (Array.isArray(yearBucket)) {
            const entry = yearBucket.find(d => d && d.type === ds) || yearBucket[0]
            inc = entry ? (entry.hh_income || entry.income || 0) : 0
          } else if (yearBucket && typeof yearBucket === 'object') {
            inc = yearBucket.hh_income || yearBucket.income || 0
          }
          return { year: yr, income: Number(inc) || 0 }
        })
      }
      if (incomeData.length > 0) {
        return incomeData
      }
    }

    return incomeData
  }

  @action resetData () {
    this.populationData = null
    this.populationTrends = []
    this.maleFemaleData = null
    this.density = null
    this.size = null
    this.areaUnit = 'km²'
    this.areaKm = null
    this.areaMi = null
    this.totalPopulation = null
    this.growthRate = null
    this.years = []
    this.latestYear = null
    this.error = null
    this.polygonData = []
    this.showPolygonLines = false
    this.showIndividualPolygonLines = false
    this.selectedLine = null
    this.selectedLinePopulationDatas = null
    this.singlePolygonData = null
    this.singlePolygonId = null
    this.noData = false
    this.incomeSelectedYear = 2021
    this.populationSelectedYear = null
    this.showIncomePolygonData = false
    this.selectedIncomePoint = null
    this.originalTargetGroupName = null
    this.stopWorldpopPolling()
    this.worldpopAvailable = false
    this.targetGroupWorldpopAvailable = false
    this.rawSinglePolygonData = null
    this.rawPolygonData = null
    this.rawTargetGroupData = null
    this.pyramidLoading = false
    this.clearSinglePolygon()
  }

  syncPopulationDatasetFromAppDefaults () {
    try {
      const dsName = dataStore.defaultDsName === 'worldpop' ? 'worldpop' : 'landscan'
      this.populationDataset = dsName
    } catch (e) {
      this.populationDataset = 'landscan'
    }
  }

  @action setWorldpopAvailable (worldpopExists) {
    this.worldpopAvailable = worldpopExists
    // if (!this.worldpopAvailable && this.populationDataset === 'worldpop') {
    //   this.setPopulationDataset('landscan')
    // }
  }

  @action stopWorldpopPolling () {
    this._worldpopPollGeneration += 1
    this.worldpopLoading = false
  }

  @action startWorldpopPolling ({ targetGroupId, geoPolygonId, scope = 'standalone' } = {}) {
    if (targetGroupId == null && geoPolygonId == null) return
    this._worldpopPollGeneration += 1
    const generation = this._worldpopPollGeneration
    this.worldpopLoading = true
    this.worldpopAvailable = false
    this._runWorldpopPollLoop(generation, { targetGroupId, geoPolygonId, scope }, 0)
  }

  async _runWorldpopPollLoop (generation, params, attempt) {
    if (generation !== this._worldpopPollGeneration) return

    try {
      const res = await api.pollWorldpopTask(params, {
        cancelToken: this.cancelSource ? this.cancelSource.token : undefined
      })
      const data = res.data || {}

      if (generation !== this._worldpopPollGeneration) return

      if (data.status === 'PENDING') {
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000)
        if (attempt < 84) {
          setTimeout(() => this._runWorldpopPollLoop(generation, params, attempt + 1), delay)
        } else {
          this.worldpopLoading = false
          this.setWorldpopAvailable(false)
        }
        return
      }
      if (data.status === 'SUCCESS' && typeof data.worldpop_exists === 'boolean') {
        if (data.worldpop_exists) {
          try {
            if (params.geoPolygonId) {
              const fetchOptions = this.lastShapePopulationOptions || { isZipCode: false, isAdminBoundary: false }
              const response = await this.fetchPopulationDataResponseForShape(
                params.geoPolygonId,
                fetchOptions,
                {
                  headers: { 'Ds-Name': 'worldpop' },
                  cancelToken: this.cancelSource ? this.cancelSource.token : undefined
                }
              )
              if (generation === this._worldpopPollGeneration) {
                const worldpopPolygons = this.extractPopulationGeometryList(response.data)

                if (params.scope === 'targetGroupLine') {
                  // that state belongs to the target group and must survive this refresh.
                  if (this.rawPolygonData) {
                    this.rawPolygonData = this.mergePolygons(this.rawPolygonData, worldpopPolygons)
                  }
                  const mergedRaw = this.rawPolygonData && this.rawPolygonData.find(
                    p => String(p.id) === String(params.geoPolygonId)
                  )
                  if (mergedRaw) {
                    const filtered = this.getFilteredPolygon(mergedRaw)
                    this.polygonData = this.polygonData.map(polygon =>
                      String(polygon.id) === String(params.geoPolygonId)
                        ? { ...polygon, population_datas: filtered.population_datas || polygon.population_datas }
                        : polygon
                    )
                    if (this.populationDataset === 'worldpop' && String(this.selectedLine) === String(params.geoPolygonId)) {
                      this.selectedLinePopulationDatas = filtered.population_datas || this.selectedLinePopulationDatas
                      this.updateTotalPopulationForSelectedYear()
                      this.updateDerivedForSelectedYear()
                    }
                  }
                } else {
                  this.rawSinglePolygonData = this.mergePolygons(this.rawSinglePolygonData, worldpopPolygons)

                  // If user somehow already switched to worldpop, re-filter it now
                  if (this.populationDataset === 'worldpop') {
                    this.singlePolygonData = this.getFilteredPolygon(this.rawSinglePolygonData && this.rawSinglePolygonData.length > 0 ? this.rawSinglePolygonData[0] : null)
                    this.updateTotalPopulationForSelectedYear()
                    this.updateDerivedForSelectedYear()
                  }
                }
              }

              // Also fetch WorldPop geopolygon population_datas for ASB pyramid behind the scenes
              try {
                const wpGeopolygonRes = await api.getGeopolygon(params.geoPolygonId, {
                  headers: { 'Ds-Name': 'worldpop' },
                  cancelToken: this.cancelSource ? this.cancelSource.token : undefined
                })
                const wpPd = wpGeopolygonRes.data && wpGeopolygonRes.data.population_datas
                if (wpPd) {
                  if (selectionStore && selectionStore.item && String(selectionStore.item.id) === String(params.geoPolygonId)) {
                    selectionStore.item.population_datas = mergePopulationDatas(selectionStore.item.population_datas, wpPd) || wpPd
                  }
                  if (this.singlePolygonData && String(this.singlePolygonId) === String(params.geoPolygonId)) {
                    this.singlePolygonData.population_datas = mergePopulationDatas(this.singlePolygonData.population_datas, wpPd) || wpPd
                  }
                  if (params.scope === 'targetGroupLine' && String(this.selectedLine) === String(params.geoPolygonId)) {
                    this.selectedLinePopulationDatas = mergePopulationDatas(this.selectedLinePopulationDatas, wpPd) || wpPd
                  }
                }
              } catch (wpGeoErr) {
                console.warn('Failed to fetch background worldpop geopolygon population_datas', wpGeoErr)
              }
            } else if (params.targetGroupId) {
              const tgResponse = await api.getPopDataForTargetGroup(params.targetGroupId, {
                headers: { 'Ds-Name': 'worldpop' },
                cancelToken: this.cancelSource ? this.cancelSource.token : undefined
              })
              const polyResponse = await api.getDataForAllPolygons(params.targetGroupId, {
                headers: { 'Ds-Name': 'worldpop' },
                cancelToken: this.cancelSource ? this.cancelSource.token : undefined
              })
              if (generation === this._worldpopPollGeneration) {
                const worldpopTgData = tgResponse.data || {}
                if (this.rawTargetGroupData) {
                  this.rawTargetGroupData = {
                    ...this.rawTargetGroupData,
                    data: [...(this.rawTargetGroupData.data || []), ...(worldpopTgData.data || [])]
                  }
                }

                const polyData = polyResponse.data
                const worldpopPolygons = Array.isArray(polyData) ? polyData : (polyData && polyData.results ? polyData.results : [])
                this.rawPolygonData = this.mergePolygons(this.rawPolygonData, worldpopPolygons)

                if (this.populationDataset === 'worldpop' && this.rawPolygonData) {
                  this.polygonData = this.rawPolygonData.map((polygonRaw, index) => {
                    const polygonColors = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1']
                    const polygon = this.getFilteredPolygon(polygonRaw)
                    polygon.color = polygon.color || polygonColors[index % polygonColors.length]
                    return polygon
                  })
                  // Refresh aggregate totals only — this is a background refresh of an
                  // existing target group's data, not an ad-hoc multi-select, so it must
                  // not rename the target group or collapse the individual-lines view.
                  this.refreshPolygonAggregatesFromRawData()
                }
              }
            }
          } catch (err) {
            console.error('Failed to fetch appended worldpop data:', err)
          }
        }

        if (generation === this._worldpopPollGeneration) {
          if (params.targetGroupId && data.worldpop_exists) {
            this.targetGroupWorldpopAvailable = true
          }
          if (params.scope === 'targetGroupLine') {
            if (String(this.selectedLine) === String(params.geoPolygonId)) {
              this.setWorldpopAvailable(data.worldpop_exists)
            }
          } else {
            this.setWorldpopAvailable(data.worldpop_exists)
          }
        }
      }
      if (generation === this._worldpopPollGeneration) {
        this.worldpopLoading = false
      }
    } catch (err) {
      console.warn('worldpop-task poll failed:', err)
      runInAction(() => {
        if (generation !== this._worldpopPollGeneration) return
        this.worldpopLoading = false
      })
    }
  }

  @action async setPopulationDataset (dsName) {
    const isChanging = this.populationDataset !== dsName
    if (!isChanging && String(axios.defaults.headers.common['Ds-Year']) === String(dsName === 'landscan' ? 2016 : 2024)) {
      this.incomeSelectedYear = dsName === 'landscan' ? '2021' : '2024'
      return
    }
    try {
      const dsYear = dsName === 'landscan' ? 2016 : 2024
      // Updates global Ds-Name/Ds-Year and reloads region analysis for geopolygon,
      // admin boundary, zip boundary, and target group (same as Date Settings).
      this.populationDataset = dsName
      await uiStore.resetDataWsParams(dsYear, dsName)
    } catch (e) {
      // no-op
      console.error(e)
    }
    this.incomeSelectedYear = dsName === 'landscan' ? '2021' : '2024'
    try {
      if (this.singlePolygonId != null && this.rawSinglePolygonData) {
        await this.fetchDataForSinglePolygon(this.singlePolygonId, this.targetGroupName, this.lastShapePopulationOptions, this.rawSinglePolygonData, true)
      } else if (this.targetGroupId != null && this.rawPolygonData && this.rawTargetGroupData) {
        await this.fetchPopulationDataForTargetGroup(this.targetGroupId, this.targetGroupName, this.rawTargetGroupData, this.rawPolygonData, true)
      } else if (this.rawPolygonData && this.rawPolygonData.length > 0) {
        // user had multiple polygons added, re-process manually
        this.polygonData = this.rawPolygonData.map((polygonRaw, index) => {
          const polygonColors = [
            '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
            '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
          ]
          const polygon = this.getFilteredPolygon(polygonRaw)
          const color = polygon.color || polygonColors[index % polygonColors.length]

          let trends = []
          if (polygon.stats && polygon.stats.total_population) {
            trends = polygon.stats.total_population.map(item => ({
              year: item.year,
              population: item.value || 0
            }))
          }

          let incomeData = []
          if (polygon.stats && polygon.stats.hh_income) {
            incomeData = polygon.stats.hh_income.map(item => ({
              year: item.year,
              income: item.value || 0
            }))
          }

          let maleFemaleData = null
          if (polygon.stats) {
            const latestMalePop = polygon.stats.male_population && polygon.stats.male_population.length > 0
              ? polygon.stats.male_population[polygon.stats.male_population.length - 1]
              : null
            const latestFemalePop = polygon.stats.female_population && polygon.stats.female_population.length > 0
              ? polygon.stats.female_population[polygon.stats.female_population.length - 1]
              : null
            if (latestMalePop || latestFemalePop) {
              maleFemaleData = {
                male: latestMalePop ? latestMalePop.value : 0,
                female: latestFemalePop ? latestFemalePop.value : 0
              }
            }
          }

          return {
            id: polygon.id,
            name: polygon.name || `Polygon ${polygon.id}`,
            population_datas: polygon.population_datas || null,
            trends,
            incomeData,
            malePopulationData: polygon.stats && polygon.stats.male_population ? polygon.stats.male_population : [],
            femalePopulationData: polygon.stats && polygon.stats.female_population ? polygon.stats.female_population : [],
            maleFemaleData,
            color,
            areaSqkm: polygon.stats && (polygon.stats.area_sqkm || polygon.stats.area_km) ? (polygon.stats.area_sqkm || polygon.stats.area_km) : null,
            areaSqmi: polygon.stats && (polygon.stats.area_sqmi || polygon.stats.area_mi) ? (polygon.stats.area_sqmi || polygon.stats.area_mi) : null
          }
        })
        this.computeAggregateDataFromPolygons()
        this.showIncomePolygonData = true
      } else {
        await this.reloadPeopleTabForCurrentSelection()
      }
    } finally {
      if (isChanging) {
        this.setLoading(false)
      }
    }
  }

  /**
   * Refetch People tab population-data for the active target group or shape.
   * Routes to population_api endpoints: target-groups, geometries (polygon),
   * admin-boundaries, zip-boundaries.
   */
  @action async reloadPeopleTabForCurrentSelection () {
    const { getShapePopulationFetchOptions } = require('../utils')
    const { selectionTypes } = require('../constants')

    if (this.singlePolygonId != null) {
      await this.fetchDataForSinglePolygon(
        this.singlePolygonId,
        this.targetGroupName,
        this.lastShapePopulationOptions || { isZipCode: false, isAdminBoundary: false }
      )
      return
    }

    const selectionItem = selectionStore.item
    const selectedType = selectionStore.type
    const shapeTypes = [
      selectionTypes.GEOPOLYGON,
      selectionTypes.POINT_BUFFER,
      selectionTypes.LINE,
      selectionTypes.ISOCHRONE,
      selectionTypes.BOUNDARY,
      selectionTypes.ZIP_CODE_BOUNDARY
    ]
    const shapeId = selectionItem &&
      (selectionItem.id || selectionItem.inner_id || selectionItem.remote_db_id)

    if (selectionItem && shapeId && shapeTypes.includes(selectedType)) {
      await this.fetchDataForSinglePolygon(
        shapeId,
        selectionItem.name,
        getShapePopulationFetchOptions(selectionItem)
      )
      return
    }

    if (this.targetGroupId != null && this.targetGroupName) {
      await this.fetchPopulationDataForTargetGroup(this.targetGroupId, this.targetGroupName)
      return
    }

    const targetGroupFromSelection = selectedType === selectionTypes.TARGET_GROUP && selectionItem
      ? selectionItem
      : null
    const targetGroupFromContext = selectionStore.data && selectionStore.data.group
      ? selectionStore.data.group
      : null
    const targetGroup = targetGroupFromSelection || targetGroupFromContext

    if (targetGroup && targetGroup.pk) {
      await this.fetchPopulationDataForTargetGroup(
        targetGroup.pk,
        targetGroup.name || 'Target Group'
      )
    }
  }

  @action async refetchCurrentPeopleData () {
    await this.reloadPeopleTabForCurrentSelection()
  }

  @action setAggSelector (selector) {
    this.aggSelector = selector
    // Reset to showing whole group view (not individual shapes) when selector changes
    this.showIndividualPolygonLines = false
    this.showIncomePolygonData = false
    this.selectedLine = null
    this.selectedLinePopulationDatas = null
    this.recalculateDataBasedOnSelector()
  }

  @action recalculateDataBasedOnSelector () {
    this.updateTotalPopulationForSelectedYear()
    // this.updateGrowthRateForSelectedYear()
    this.updateDerivedForSelectedYear()
  }

  @action setIncomeSelectedYear (year = 2021) {
    const normalizedYear = year === null || year === undefined ? null : year.toString()
    this.incomeSelectedYear = normalizedYear
    this.populationSelectedYear = normalizedYear
    this.updateTotalPopulationForSelectedYear()
    this.updateDerivedForSelectedYear()
    // this.updateGrowthRateForSelectedYear()
  }

  @action setPopulationSelectedYear (year) {
    const normalizedYear = year === null || year === undefined ? null : year.toString()
    this.populationSelectedYear = normalizedYear
    this.incomeSelectedYear = normalizedYear
    this.updateTotalPopulationForSelectedYear()
    this.updateDerivedForSelectedYear()
    // this.updateGrowthRateForSelectedYear()
    if (selectionStore && selectionStore.item && selectionStore.item.loadGeopolygonData && normalizedYear) {
      const pd = selectionStore.item.population_datas
      const yearBucket = pd && pd[normalizedYear]
      if (!yearBucket || (Array.isArray(yearBucket) && yearBucket.length === 0)) {
        selectionStore.item.loadGeopolygonData(false, null, normalizedYear)
      }
    }
  }

  @action updateDerivedForSelectedYear () {
    const selectedYearNum = this.getSelectedPopulationYearNumber()
    if (!selectedYearNum) return

    // Selected individual shape — male/female + density from population_datas[year]
    if (this.selectedLine != null && this.showIndividualPolygonLines) {
      const yearData = this.getYearPopulationRecordFromDatas(
        this.getPopulationDatasForSelectedShape(),
        selectedYearNum
      )
      if (yearData) {
        this.maleFemaleData = {
          male: yearData.male || yearData.mal_tot || 0,
          female: yearData.female || yearData.fem_tot || 0
        }
        const polygon = this.polygonData && this.polygonData.find(
          p => String(p.id) === String(this.selectedLine)
        )
        const areaSqkm = this.getRegionAnalysisAreaKmForShape(polygon.id, { isZipCode: polygon.type === 'ZIP_BOUNDARY', isAdminBoundary: polygon.type === 'ADMIN_BOUNDARY' })
        // const areaSqkm = polygon && polygon.areaSqkm
        //   ? polygon.areaSqkm
        //   : (this.singlePolygonData && (
        //     (this.singlePolygonData.stats && (this.singlePolygonData.stats.area_sqkm || this.singlePolygonData.stats.area_km)) ||
        //     this.singlePolygonData.area_sqkm ||
        //     this.singlePolygonData.area_km
        //   )) || null
        if (areaSqkm) {
          const ratio = (yearData.sum_pop || yearData.sum || 0) / areaSqkm
          this.density = Number.isFinite(ratio) ? Number(ratio.toFixed(3)) : null
        }
        return
      }
    }

    // Single polygon data takes precedence
    if (this.singlePolygonData && this.singlePolygonData.stats) {
      const stats = this.singlePolygonData.stats
      const maleYear = stats.male_population && stats.male_population.find(d => String(d.year) === String(selectedYearNum))
      const femaleYear = stats.female_population && stats.female_population.find(d => String(d.year) === String(selectedYearNum))
      const popYear = stats.total_population && stats.total_population.find(d => String(d.year) === String(selectedYearNum))
      // const areaSqkm = stats.area_sqkm || stats.area_km || null
      const areaSqkm = this.getRegionAnalysisAreaKmForShape(this.singlePolygonId, this.singlePolygonType)
      this.maleFemaleData = {
        male: maleYear ? (maleYear.value || 0) : 0,
        female: femaleYear ? (femaleYear.value || 0) : 0
      }

      if (areaSqkm && popYear) {
        const ratio = (popYear.value || 0) / areaSqkm
        this.density = Number.isFinite(ratio) ? Number(ratio.toFixed(0)) : null
      }
      return
    }

    // Single polygon payload can also come from geopolygon endpoint where
    // values are in population_datas[year][0].
    if (this.singlePolygonData && this.singlePolygonData.population_datas) {
      const yearBucket = this.singlePolygonData.population_datas[selectedYearNum.toString()]
      const ds = this.populationDataset || 'landscan'
      const yearData = Array.isArray(yearBucket) && yearBucket.length > 0 ? (yearBucket.find(d => d.type === ds) || yearBucket[0]) : null
      if (yearData) {
        this.maleFemaleData = {
          male: yearData.male || yearData.mal_tot || 0,
          female: yearData.female || yearData.fem_tot || 0
        }

        // const areaSqkm = this.singlePolygonData.stats && (this.singlePolygonData.stats.area_sqkm || this.singlePolygonData.stats.area_km)
        //   ? (this.singlePolygonData.stats.area_sqkm || this.singlePolygonData.stats.area_km)
        //   : (this.singlePolygonData.area_sqkm || this.singlePolygonData.area_km || null)
        const areaSqkm = this.getRegionAnalysisAreaKmForShape(this.singlePolygonId, this.singlePolygonType)
        console.log('2nd condition: AREA SQKM: ', areaSqkm)
        if (areaSqkm) {
          const ratio = (yearData.sum_pop || yearData.sum || 0) / areaSqkm
          this.density = Number.isFinite(ratio) ? Number(ratio.toFixed(3)) : null
        }
        return
      }
    }

    // Target group data from API
    if (this.populationData && this.populationData.data && this.populationData.data.length > 0) {
      const yearData = this.populationData.data.find(d => d.year === selectedYearNum)
      const dissolvedData = this.populationData.dissolved_data &&
        this.populationData.dissolved_data.find(d => d.year === selectedYearNum)

      if (yearData) {
        if (this.aggSelector === 'dissolved' && dissolvedData) {
          this.maleFemaleData = {
            male: dissolvedData.total_male_population || 0,
            female: dissolvedData.total_female_population || 0
          }
        } else if (this.aggSelector === 'avg') {
          this.maleFemaleData = {
            male: yearData.average_male_population || 0,
            female: yearData.average_female_population || 0
          }
        } else {
          this.maleFemaleData = {
            male: yearData.total_male_population || yearData.male_population || 0,
            female: yearData.total_female_population || yearData.female_population || 0
          }
        }
        if (this.aggSelector === 'avg') {
          const avgDensity = this.calculateAverageOfRatios(this.polygonData, selectedYearNum)
          this.density = avgDensity !== null
            ? avgDensity
            : (yearData.average_density || null)
        } else {
          const areaKm = this.aggSelector === 'dissolved' ? this.populationData.sp_area_km : this.populationData.area_km
          const count_polygons = this.populationData.count_polygons || 1
          const average_population = this.aggSelector === 'dissolved' ? dissolvedData.total_population : yearData.average_population
          const average_density = this.calculateRatioOfAverages(average_population, areaKm, count_polygons)
          this.density = Number.isFinite(average_density) ? Number(average_density.toFixed(2)) : null
          // const totalPopulation = this.aggSelector === 'dissolved' && dissolvedData ? (dissolvedData.total_population || 0) : (yearData.average_population || 0)
          // if (this.aggSelector === 'dissolved' && dissolvedData && dissolvedData.total_density !== undefined && dissolvedData.total_density !== null) {
          //   this.density = totalPopulation /
          // } else if (yearData.total_density !== undefined && yearData.total_density !== null) {
          //   this.density = yearData.total_density
          // } else if (areaKm) {
          //   const ratio = totalPopulation / areaKm
          //   this.density = Number.isFinite(ratio) ? Number(ratio.toFixed(3)) : null
          // } else {
          //   this.density = null
          // }
        }
      }
    }
  }

  @action setSelectedIncomePoint (pointId) {
    this.selectedIncomePoint = pointId
  }

  @action clearSelectedIncomePoint () {
    this.selectedIncomePoint = null
  }

  @action setShowIncomePolygonData (show) {
    this.showIncomePolygonData = show
  }

  @action restoreSummaryWorldpopAvailability () {
    this.stopWorldpopPolling()
    if (this.targetGroupId != null) {
      const tg = dataStore.targetGroups && dataStore.targetGroups.find(g => String(g.pk) === String(this.targetGroupId))
      const hasWpInTg = Boolean(tg && tg.worldpop_exists)
      const hasWpInTgData = Boolean(
        this.rawTargetGroupData &&
        Array.isArray(this.rawTargetGroupData.data) &&
        this.rawTargetGroupData.data.some(d => d && (d.ds_name === 'worldpop' || hasWorldpopInData(d.population_datas)))
      )
      const hasWpInPolygons = Boolean(
        this.rawPolygonData &&
        Array.isArray(this.rawPolygonData) &&
        this.rawPolygonData.some(p => p && (p.worldpop_exists || (p.worldpop_stats && p.worldpop_stats.length > 0) || hasWorldpopInData(p.population_datas)))
      )

      if (this.targetGroupWorldpopAvailable || hasWpInTg || hasWpInTgData || hasWpInPolygons) {
        this.worldpopAvailable = true
        this.worldpopLoading = false
      } else {
        this.worldpopAvailable = false
        this.worldpopLoading = true
        this.startWorldpopPolling({ targetGroupId: this.targetGroupId })
      }
    }
  }

  @action showIncomeIndividualShapes () {
    this.showIncomePolygonData = true
    this.showPolygonLines = true
    this.showIndividualPolygonLines = true
  }

  @action setSelectedLine (lineId, year = null) {
    this.selectedLine = lineId
    if (this.showIndividualPolygonLines && lineId != null) {
      this.selectedLinePopulationDatas = null
      this.loadPopulationDatasForSelectedGeopolygon(lineId).then()
    } else {
      this.selectedLinePopulationDatas = null
      if (!this.showIndividualPolygonLines) {
        this.restoreSummaryWorldpopAvailability()
      }
    }
    if (year !== null && year !== undefined) {
      this.populationSelectedYear = year.toString()
      this.incomeSelectedYear = year.toString()
    }
    const yearNum = this.getSelectedPopulationYearNumber() || this.getLatestYearNumber()
    const polygon = this.polygonData && this.polygonData.find(p => String(p.id) === String(lineId))
    if (polygon && yearNum) {
      const density = this.calculatePolygonDensity(polygon, yearNum)
      if (density !== null) this.density = density
    }
    if (polygon) {
      const polygonAreaKm = polygon.areaSqkm || null
      const polygonAreaMi = polygon.areaSqmi || (polygonAreaKm ? (polygonAreaKm * 0.386102) : null)
      this.areaKm = polygonAreaKm
      this.areaMi = polygonAreaMi
      if (this.areaUnit === 'mi²' && polygonAreaMi !== null && polygonAreaMi !== undefined) {
        this.size = `${polygonAreaMi.toFixed(2)} mi²`
      } else if (polygonAreaKm !== null && polygonAreaKm !== undefined) {
        this.size = `${polygonAreaKm.toFixed(2)} km²`
      } else {
        this.size = null
      }
    }
    this.updateTotalPopulationForSelectedYear()
    this.updateDerivedForSelectedYear()
    // this.updateGrowthRateForSelectedYear()
  }

  async loadPopulationDatasForSelectedGeopolygon (lineId) {
    if (lineId == null || !this.showIndividualPolygonLines) return
    const localCancelSource = this.getNewCancelSource()
    try {
      runInAction(() => { this.pyramidLoading = true })
      this.startWorldpopPolling({ geoPolygonId: lineId, scope: 'targetGroupLine' })
      const [lsRes, wpRes] = await Promise.allSettled([
        api.getGeopolygon(lineId, {
          cancelToken: localCancelSource.token,
          headers: { 'Ds-Name': 'landscan', 'Ds-Year': '2016' }
        }),
        api.getGeopolygon(lineId, {
          cancelToken: localCancelSource.token,
          headers: { 'Ds-Name': 'worldpop', 'Ds-Year': '2024' }
        })
      ])
      if (this.cancelSource !== localCancelSource) return
      const lsPd = lsRes.status === 'fulfilled' && lsRes.value && lsRes.value.data ? lsRes.value.data.population_datas : null
      const wpPd = wpRes.status === 'fulfilled' && wpRes.value && wpRes.value.data ? wpRes.value.data.population_datas : null
      const pd = mergePopulationDatas(lsPd, wpPd) || lsPd || wpPd
      runInAction(() => {
        if (this.selectedLine === lineId) {
          this.selectedLinePopulationDatas = pd || null
          this.updateTotalPopulationForSelectedYear()
          this.updateDerivedForSelectedYear()
          // this.updateGrowthRateForSelectedYear()
        }
      })
    } catch (e) {
      if (this.cancelSource === localCancelSource) {
        if (!axios.isCancel(e)) {
          console.warn('Failed to load geopolygon population_datas for ASB', e)
        }
        runInAction(() => {
          if (this.selectedLine === lineId) {
            this.selectedLinePopulationDatas = null
          }
        })
      }
    } finally {
      if (this.cancelSource === localCancelSource) {
        runInAction(() => { this.pyramidLoading = false })
      }
    }
  }

  @action clearSelectedLine () {
    this.selectedLine = null
    if (this.populationData && this.populationData.area_km) {
      this.areaKm = this.populationData.area_km
      this.areaMi = this.populationData.area_mi || null
      if (this.areaUnit === 'mi²' && this.areaMi !== null && this.areaMi !== undefined) {
        this.size = `${this.areaMi.toFixed(2)} mi²`
      } else {
        this.size = `${this.areaKm.toFixed(2)} km²`
      }
    }
    this.recalculateDataBasedOnSelector()
  }

  @action showShapeLines () {
    this.showPolygonLines = true
    this.showIndividualPolygonLines = true
    this.showIncomePolygonData = true
  }

  @action updateLatestYear () {
    if (this.years && this.years.length > 0) {
      const maxYear = Math.max(...this.years.map(y => {
        const val = y && (y.label || y.val || y.id || y)
        return parseInt(val, 10) || 0
      }))
      if (maxYear > 0) {
        this.latestYear = String(maxYear)
        return
      }
    }
    this.latestYear = this.populationDataset === 'landscan' ? '2024' : '2030'
  }

  @action setSinglePolygon (polygonId, polygonData, shapeOptions) {
    // Preserve original target group name before setting single polygon
    if (!this.originalTargetGroupName && this.targetGroupName) {
      this.originalTargetGroupName = this.targetGroupName
    }
    this.singlePolygonId = polygonId
    this.singlePolygonType = shapeOptions
    this.singlePolygonData = polygonData
    this.showPolygonLines = false
    this.showIndividualPolygonLines = false
    this.populationSelectedYear = '2024'
    this.incomeSelectedYear = '2021'
  }

  @action clearSinglePolygon () {
    this.singlePolygonId = null
    this.singlePolygonData = null
    this.rawSinglePolygonData = null
    this.populationSelectedYear = '2024'
    this.incomeSelectedYear = '2021'
    // Restore original target group name if it was preserved
    if (this.originalTargetGroupName) {
      this.targetGroupName = this.originalTargetGroupName
      this.originalTargetGroupName = null
    }
  }

  @action toggleAreaUnit () {
    if (this.areaUnit === 'km²') {
      this.areaUnit = 'mi²'
    } else {
      this.areaUnit = 'km²'
    }
    if (this.areaKm != null) {
      this.applyPeopleTabSizeFromAreaKm(this.areaKm)
    }
  }

  /** Same km² as Region Analysis Summary (`analysis.area` only). */
  regionAnalysisAreaKmFromItem (item) {
    const analysis = item && item.analysis
    if (analysis && analysis.valid && Number.isFinite(analysis.area)) {
      return analysis.area
    }
    return null
  }

  getMapShapeByGeometryId (geometryId, shapeOptions = {}) {
    try {
      const id = String(geometryId)
      for (const tg of dataStore.targetGroups) {
        if (shapeOptions.isZipCode) {
          const shape = tg.zipCodeBoundaries.find(
            b => String(b.remote_db_id) === id || String(b.inner_id) === id
          )
          if (shape) return shape
        } else if (shapeOptions.isAdminBoundary) {
          const shape = tg.boundaries.find(
            b => String(b.remote_db_id) === id || String(b.inner_id) === id
          )
          if (shape) return shape
        } else {
          const shape = tg.geopolygons.find(g => String(g.id) === id)
          if (shape) return shape
        }
      }
    } catch (e) {
      return null
    }
    return null
  }

  getRegionAnalysisAreaKmForShape (geometryId, shapeOptions = {}) {
    return this.regionAnalysisAreaKmFromItem(
      this.getMapShapeByGeometryId(geometryId, shapeOptions)
    )
  }

  getRegionAnalysisAreaKmForTargetGroup (targetGroupId) {
    try {
      const tg = dataStore.targetGroups.find(g => String(g.pk) === String(targetGroupId))
      return this.regionAnalysisAreaKmFromItem(tg)
    } catch (e) {
      return null
    }
  }

  @action syncPeopleTabSizeFromRegionAnalysis () {
    try {
      const { selectionStore } = require('./UIStore')
      let km2 = this.regionAnalysisAreaKmFromItem(selectionStore.item)
      if (km2 == null && this.singlePolygonId != null) {
        km2 = this.getRegionAnalysisAreaKmForShape(this.singlePolygonId, {})
      }
      if (km2 == null && this.targetGroupId != null) {
        km2 = this.getRegionAnalysisAreaKmForTargetGroup(this.targetGroupId)
      }
      if (km2 != null) {
        this.applyPeopleTabSizeFromAreaKm(km2)
      }
    } catch (e) {
      // no-op
    }
  }

  @action applyPeopleTabSizeFromAreaKm (areaKm) {
    if (areaKm == null || !Number.isFinite(areaKm) || areaKm <= 0) {
      this.areaKm = null
      this.areaMi = null
      this.size = null
      return
    }
    this.areaKm = areaKm
    this.areaMi = areaKm * 0.386102
    if (this.areaUnit === 'mi²') {
      this.size = `${this.areaMi.toFixed(2)} mi²`
    } else {
      this.size = `${areaKm.toFixed(2)} km²`
    }
  }

  /**
   * Replace population API `area_km` with Region Analysis `analysis.area` when available.
   */
  patchTargetGroupChartAreaFromMapGeometry (targetGroupId, chartData) {
    if (!chartData || targetGroupId == null) return chartData
    const km2 = this.getRegionAnalysisAreaKmForTargetGroup(targetGroupId)
    if (!km2) return chartData

    return {
      ...chartData,
      area_km: km2,
      area_mi: km2 * 0.386102
    }
  }

  /**
   * When population_api returns data: [] but polygons have stats, rebuild the yearly
   * target-group series by summing / averaging per-polygon geometries data.
   */
  buildYearlyDataFromPolygonGeometries (polygons) {
    if (!polygons || polygons.length === 0) return []

    const yearSet = new Set()
    polygons.forEach((polygon) => {
      const sources = [
        polygon.trends,
        polygon.malePopulationData,
        polygon.femalePopulationData,
        polygon.incomeData
      ]
      sources.forEach((series) => {
        if (Array.isArray(series)) {
          series.forEach((item) => {
            if (item && item.year != null) yearSet.add(item.year)
          })
        }
      })
    })

    const polygonCount = polygons.length
    return Array.from(yearSet).sort((a, b) => a - b).map((year) => {
      let totalPopulation = 0
      let totalMale = 0
      let totalFemale = 0
      let totalIncome = 0

      polygons.forEach((polygon) => {
        const popTrend = polygon.trends && polygon.trends.find(t => t.year === year)
        if (popTrend) totalPopulation += popTrend.population || 0

        const maleItem = polygon.malePopulationData &&
          polygon.malePopulationData.find(item => item.year === year)
        if (maleItem) totalMale += maleItem.value || 0

        const femaleItem = polygon.femalePopulationData &&
          polygon.femalePopulationData.find(item => item.year === year)
        if (femaleItem) totalFemale += femaleItem.value || 0

        const incomeItem = polygon.incomeData &&
          polygon.incomeData.find(item => item.year === year)
        if (incomeItem) totalIncome += incomeItem.income || 0
      })

      return {
        year,
        total_population: totalPopulation,
        average_population: polygonCount > 0 ? totalPopulation / polygonCount : 0,
        total_male_population: totalMale,
        average_male_population: polygonCount > 0 ? totalMale / polygonCount : 0,
        total_female_population: totalFemale,
        average_female_population: polygonCount > 0 ? totalFemale / polygonCount : 0,
        total_income: totalIncome,
        average_income: polygonCount > 0 ? totalIncome / polygonCount : 0,
        total_density: 0,
        average_density: 0
      }
    })
  }

  applyTargetGroupChartData (chartData) {
    if (!chartData || !chartData.data || chartData.data.length === 0) return false

    const hasTypedSeries = chartData.data.some(item => item && item.type)
    const yearlyData = hasTypedSeries ? this.getFilteredYearlyData(chartData.data) : chartData.data

    if (!yearlyData || yearlyData.length === 0) {
      this.noData = true
      this.populationData = { ...chartData, data: [] }
      this.populationTrends = []
      this.years = []
      this.latestYear = null
      this.incomeSelectedYear = null
      this.populationSelectedYear = null
      this.totalPopulation = null
      this.growthRate = null
      this.density = null
      this.size = null
      return false
    }

    this.noData = false
    this.populationTrends = yearlyData.map(item => ({
      year: item.year,
      total_population: item.total_population || 0,
      average_population: item.average_population || 0,
      population: item.total_population || item.average_population || 0
    }))

    const latest_population = this.populationTrends[this.populationTrends.length - 1].total_population
    const first_population = this.populationTrends[0].total_population

    const growthRate = ((latest_population - first_population) / first_population * 100)
    this.growthRate = growthRate.toFixed(1)

    this.years = yearlyData.map(item => ({
      id: item.year,
      label: item.year.toString(),
      val: item.year
    }))
    // console.log('pop trend are', this.populationTrends)

    if (this.years.length > 0) {
      this.updateLatestYear()
      this.incomeSelectedYear = '2021'
      this.populationSelectedYear = this.latestYear
    }

    this.populationData = { ...chartData, data: yearlyData }
    this.updateTotalPopulationForSelectedYear()
    // this.updateGrowthRateForSelectedYear()
    this.updateDerivedForSelectedYear()

    if (chartData.area_km) {
      this.applyPeopleTabSizeFromAreaKm(chartData.area_km)
    } else {
      this.areaKm = null
      this.areaMi = null
      this.size = null
    }

    return true
  }

  @catchError
  @action async fetchPopulationDataForTargetGroup (targetGroupId, targetGroupName, cachedTargetGroupData = null, cachedPolygons = null, isRefresh = false) {
    const dsName = this.populationDataset || (dataStore.defaultDsName === 'worldpop' ? 'worldpop' : 'landscan')
    if (
      this._fetchingTargetGroupId === targetGroupId &&
      this._fetchingDataset === dsName &&
      this.cancelSource && !isRefresh
    ) {
      return
    }

    // If isRefresh but the token was already cancelled (user navigated away), bail out.
    if (isRefresh && (!this.cancelSource || this.cancelSource.token.reason)) return

    const localCancelSource = isRefresh ? this.cancelSource : this.getNewCancelSource()
    this._fetchingTargetGroupId = targetGroupId
    this._fetchingGeometryId = null
    this._fetchingDataset = dsName

    if (!this.populationDataset) {
      this.syncPopulationDatasetFromAppDefaults()
    }
    if (!isRefresh) {
      this.setLoading(true)
    }
    this.noData = false
    // Clear single polygon data when selecting target group
    this.clearSinglePolygon()

    // Reset to target group view (not individual shapes)
    this.showIncomePolygonData = false
    this.showIndividualPolygonLines = false
    this.showPolygonLines = false
    this.selectedLine = null
    this.selectedLinePopulationDatas = null

    // Clear previous data if selecting a different target group
    if (this.targetGroupId !== null && this.targetGroupId !== targetGroupId) {
      this.resetData()
    }

    this.error = null
    this.targetGroupId = targetGroupId
    this.targetGroupName = targetGroupName
    if (!cachedTargetGroupData && !cachedPolygons && !isRefresh) {
      this.startWorldpopPolling({ targetGroupId })
    }

    try {
      // Fetch population data for target group
      let chartDataRaw
      const targetGroup = dataStore.targetGroups.find(g => String(g.pk) === String(targetGroupId))
      const cacheKey = dsName
      let useCache = false
      let cachedPolygonsToPass = cachedPolygons

      if (!isRefresh && !cachedTargetGroupData && !cachedPolygons) {
        if (targetGroup && targetGroup.sidebarCache && targetGroup.sidebarCache.has(cacheKey)) {
          useCache = true
        }
      }

      const loadData = async (forceFetch = false) => {
        if (useCache && !forceFetch) {
          const cached = targetGroup.sidebarCache.get(cacheKey)
          chartDataRaw = cached.targetGroupData
          cachedPolygonsToPass = cached.polygonData
          this.rawTargetGroupData = chartDataRaw
        } else if (cachedTargetGroupData && !forceFetch) {
          chartDataRaw = cachedTargetGroupData
          this.rawTargetGroupData = chartDataRaw
        } else {
          const response = await api.getPopDataForTargetGroup(targetGroupId, { cancelToken: localCancelSource.token })
          if (this.cancelSource !== localCancelSource) return false
          chartDataRaw = response.data || {}
          this.rawTargetGroupData = chartDataRaw
        }
        return true
      }

      try {
        const success = await loadData()
        if (success === false) return
      } catch (err) {
        if (useCache) {
          console.warn('Error loading target group from cache, refetching...', err)
          targetGroup.sidebarCache.delete(cacheKey)
          useCache = false
          const success = await loadData(true)
          if (success === false) return
        } else {
          throw err
        }
      }

      const apiHasYearlyData = Boolean(chartDataRaw.data && chartDataRaw.data.length > 0)

      const hasWpInTg = Boolean(targetGroup && targetGroup.worldpop_exists)
      const hasWpInTgData = Boolean(
        chartDataRaw &&
        Array.isArray(chartDataRaw.data) &&
        chartDataRaw.data.some(d => d && (d.ds_name === 'worldpop' || hasWorldpopInData(d.population_datas)))
      )
      if (hasWpInTg || hasWpInTgData) {
        this.targetGroupWorldpopAvailable = true
        this.setWorldpopAvailable(true)
        this.worldpopLoading = false
      }

      // Start fetching polygon data asynchronously without blocking the main target group render
      runInAction(() => {
        if (!isRefresh) this.pyramidLoading = true
      })
      const polygonPromise = this.fetchDataForAllPolygons(targetGroupId, false, false, { cancelToken: localCancelSource.token }, cachedPolygonsToPass)

      if (useCache || cachedTargetGroupData || !apiHasYearlyData) {
        try {
          await polygonPromise
          runInAction(() => {
            if (this.cancelSource !== localCancelSource) return

            // Populate the cache!
            if (!useCache && !cachedTargetGroupData && !cachedPolygons && !isRefresh) {
              if (targetGroup && targetGroup.sidebarCache) {
                targetGroup.sidebarCache.set(cacheKey, {
                  targetGroupData: chartDataRaw,
                  polygonData: this.rawPolygonData
                })
              }
            }

            this.showIncomePolygonData = false
            this.showIndividualPolygonLines = false
            this.showPolygonLines = false
            this.pyramidLoading = false
          })
        } catch (polygonError) {
          runInAction(() => {
            if (this.cancelSource !== localCancelSource) return
            if (!axios.isCancel(polygonError)) {
              console.warn('Error fetching polygon data:', polygonError)
            }
            this.showIncomePolygonData = false
            this.showIndividualPolygonLines = false
            this.showPolygonLines = false
            this.pyramidLoading = false
          })
        }
      } else {
        polygonPromise
          .then(() => {
            runInAction(() => {
              if (this.cancelSource !== localCancelSource) return

              // Populate the cache!
              if (!useCache && !cachedTargetGroupData && !cachedPolygons && !isRefresh) {
                if (targetGroup && targetGroup.sidebarCache) {
                  targetGroup.sidebarCache.set(cacheKey, {
                    targetGroupData: chartDataRaw,
                    polygonData: this.rawPolygonData
                  })
                }
              }

              this.showIncomePolygonData = false
              this.showIndividualPolygonLines = false
              this.showPolygonLines = false
            })
          })
          .catch((polygonError) => {
            runInAction(() => {
              if (this.cancelSource !== localCancelSource) return
              if (!axios.isCancel(polygonError)) {
                console.warn('Error fetching polygon data:', polygonError)
              }
              this.showIncomePolygonData = false
              this.showIndividualPolygonLines = false
              this.showPolygonLines = false
            })
          })
          .finally(() => {
            runInAction(() => {
              if (this.cancelSource === localCancelSource) {
                this.pyramidLoading = false
              }
            })
          })
      }

      let chartData = { ...chartDataRaw }
      const regionAreaKm = this.getRegionAnalysisAreaKmForTargetGroup(targetGroupId)
      if (regionAreaKm) {
        chartData.area_km = regionAreaKm
        chartData.area_mi = regionAreaKm * 0.386102
      }

      const polygonCount = chartDataRaw.count_polygons || (this.polygonData && this.polygonData.length) || 0
      if (!apiHasYearlyData && polygonCount > 0 && this.polygonData && this.polygonData.length > 0) {
        const rebuilt = this.buildYearlyDataFromPolygonGeometries(this.polygonData)
        if (rebuilt.length > 0) {
          console.warn(
            'Target group API returned empty data[]; rebuilt yearly series from polygon geometries',
            { targetGroupId, polygonCount: this.polygonData.length, years: rebuilt.length }
          )
          chartData = {
            ...chartData,
            data: rebuilt,
            count_polygons: polygonCount
          }
        }
      }

      const applied = this.applyTargetGroupChartData(chartData)
      if (!applied) {
        this.noData = true
        this.populationData = chartDataRaw
        this.years = []
        this.latestYear = null
        this.incomeSelectedYear = null
        this.populationSelectedYear = null
        this.populationTrends = []
        this.maleFemaleData = null
        this.totalPopulation = null
        this.growthRate = null
        this.density = null
        this.size = null
      }
    } catch (error) {
      if (this.cancelSource === localCancelSource) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching population data:', error)
        }
        this.noData = true
        this.error = null
        this.populationData = null
        this.populationTrends = []
        this.maleFemaleData = null
        this.totalPopulation = null
        this.growthRate = null
        this.density = null
        this.size = null
        this.pyramidLoading = false
      }
    } finally {
      if (this.cancelSource === localCancelSource) {
        this._fetchingTargetGroupId = null
        this._fetchingGeometryId = null
        this._fetchingDataset = null
        // Ensure we're showing target group view, not individual shapes
        this.showIncomePolygonData = false
        this.showIndividualPolygonLines = false
        this.showPolygonLines = false
        this.syncPeopleTabSizeFromRegionAnalysis()
        this.setLoading(false)
        this.pyramidLoading = false
      }
    }
  }

  @action processPopulationData (data) {
    if (!data || !data.data || data.data.length === 0) return

    const yearlyData = this.getFilteredYearlyData(data.data)

    // Process trends
    this.populationTrends = yearlyData.map(item => ({
      year: item.year,
      population: item.total_population || 0
    }))

    // Get latest data for overview
    const latestData = yearlyData[yearlyData.length - 1]
    const firstData = yearlyData[0]

    const growthRate = ((latestData.total_population - firstData.total_population) / firstData.total_population) * 100
    this.growthRate = growthRate.toFixed(1)
    // Calculate growth rate (YoY)
    // if (data.data.length >= 2) {
    //   const previousYear = data.data[data.data.length - 2]
    //   const currentYear = latestData
    //   const prevPop = previousYear.total_population || 0
    //   const currentPop = currentYear.total_population || 0
    //   if (prevPop > 0) {
    //     const growth = ((currentPop - prevPop) / prevPop) * 100
    //     this.growthRate = growth.toFixed(1)
    //   }
    // }

    this.years = yearlyData.map(item => item.year).filter(Boolean).sort()
    this.updateLatestYear()
    this.incomeSelectedYear = '2021'
    this.populationSelectedYear = this.latestYear
    this.updateTotalPopulationForSelectedYear()

    // Process male/female data
    if (latestData.male_population !== undefined || latestData.female_population !== undefined) {
      this.maleFemaleData = {
        male: latestData.male_population || 0,
        female: latestData.female_population || 0
      }
    }

    // Process density and size (if available)
    if (latestData.density !== undefined) {
      this.density = typeof latestData.density === 'number'
        ? latestData.density.toLocaleString(undefined, { maximumFractionDigits: 3 })
        : latestData.density
    }
    if (latestData.size !== undefined) {
      this.size = latestData.size
    }
  }

  @action async fetchDataForAllPolygons (targetGroupId, showLines = false, manageLoading = true, config = {}, cachedPolygons = null) {
    if (manageLoading) this.setLoading(true)
    this.error = null

    try {
      let polygons
      if (cachedPolygons) {
        polygons = cachedPolygons
        this.rawPolygonData = polygons
      } else {
        const response = await api.getDataForAllPolygons(targetGroupId, config)
        const data = response.data
        polygons = Array.isArray(data) ? data : (data && data.results ? data.results : [])
        this.rawPolygonData = polygons
      }

      if (polygons && polygons.length > 0) {
        const polygonColors = [
          '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
          '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
        ]

        this.polygonData = polygons.map((polygonRaw, index) => {
          const polygon = this.getFilteredPolygon(polygonRaw)
          const color = polygonColors[index % polygonColors.length]
          const matchedShape = this.findTargetGroupShape(polygonRaw, index, targetGroupId)
          const resolvedName = this.resolveShapeName(polygonRaw, index, targetGroupId)

          // Process trends
          let trends = []
          if (polygon.stats && polygon.stats.total_population) {
            trends = polygon.stats.total_population.map(item => ({
              year: item.year,
              population: item.value || 0
            }))
          }

          // Process income data
          const incomeData = this.extractPolygonIncomeData(polygon, matchedShape)

          // Process male/female data (latest)
          let maleFemaleData = null
          if (polygon.stats) {
            const latestMalePop = polygon.stats.male_population && polygon.stats.male_population.length > 0
              ? polygon.stats.male_population[polygon.stats.male_population.length - 1]
              : null
            const latestFemalePop = polygon.stats.female_population && polygon.stats.female_population.length > 0
              ? polygon.stats.female_population[polygon.stats.female_population.length - 1]
              : null

            if (latestMalePop || latestFemalePop) {
              maleFemaleData = {
                male: latestMalePop ? latestMalePop.value : 0,
                female: latestFemalePop ? latestFemalePop.value : 0
              }
            }
          }
          this.setLoading(false) // Ensure loading state is reset after processing each polygon
          return {
            id: polygon.id,
            name: resolvedName,
            type: polygon.type,
            population_datas: polygon.population_datas || (matchedShape && matchedShape.population_datas) || null,
            trends,
            incomeData,
            malePopulationData: polygon.stats && polygon.stats.male_population ? polygon.stats.male_population : [],
            femalePopulationData: polygon.stats && polygon.stats.female_population ? polygon.stats.female_population : [],
            maleFemaleData,
            color,
            areaSqkm: polygon.stats && (polygon.stats.area_sqkm || polygon.stats.area_km) ? (polygon.stats.area_sqkm || polygon.stats.area_km) : null,
            areaSqmi: polygon.stats && (polygon.stats.area_sqmi || polygon.stats.area_mi) ? (polygon.stats.area_sqmi || polygon.stats.area_mi) : null
          }
        })

        if (showLines) {
          this.showPolygonLines = true
          this.showIndividualPolygonLines = true
          this.showIncomePolygonData = true
        }
      } else {
        console.warn('No polygon data available -', 'polygons length:', polygons && polygons.length)
        this.polygonData = []
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Failed to fetch polygon data:', error)
        this.error = error && error.message ? error.message : 'Failed to fetch polygon data'
      }
    } finally {
      if (manageLoading) {
        if (!config.cancelToken || !config.cancelToken.reason) {
          this.setLoading(false)
        }
      }
    }
  }

  extractPopulationGeometryList (data) {
    if (!data) return []
    if (Array.isArray(data)) return data
    if (data.results && Array.isArray(data.results)) return data.results
    if (data.stats || data.id != null || data.remote_db_id != null) return [data]
    return []
  }

  fetchPopulationDataResponseForShape (shapeId, shapeOptions = {}, config = {}) {
    const { isZipCode = false, isAdminBoundary = false } = shapeOptions
    if (isZipCode || isAdminBoundary) {
      runInAction(() => {
        this.worldpopAvailable = true
        this.worldpopLoading = false
      })
    }
    if (isZipCode) {
      return api.getPopDataForZipBoundary(shapeId, config)
    }
    if (isAdminBoundary) {
      return api.getPopDataForAdminBoundary(shapeId, config)
    }
    return api.getDataForPolygon(shapeId, config)
  }

  @action async fetchDataForSinglePolygon (geometryId, polygonName, shapeOptions = {}, cachedPolygons = null, isRefresh = false) {
    const dsName = this.populationDataset || (dataStore.defaultDsName === 'worldpop' ? 'worldpop' : 'landscan')
    if (
      this._fetchingGeometryId === geometryId &&
      this._fetchingDataset === dsName &&
      this.cancelSource && !isRefresh
    ) {
      return
    }

    // If isRefresh but the token was already cancelled (user navigated away), bail out.
    if (isRefresh && (!this.cancelSource || this.cancelSource.token.reason)) return

    const localCancelSource = isRefresh ? this.cancelSource : this.getNewCancelSource()
    this._fetchingGeometryId = geometryId
    this._fetchingTargetGroupId = null
    this._fetchingDataset = dsName

    if (!this.populationDataset) {
      this.syncPopulationDatasetFromAppDefaults()
    }
    this.lastShapePopulationOptions = shapeOptions || { isZipCode: false, isAdminBoundary: false }

    // Set the name immediately so the UI reflects the clicked item while loading
    if (polygonName) {
      this.targetGroupName = polygonName
    }

    // Clear previous data if selecting a different shape
    if (this.singlePolygonId !== null && String(this.singlePolygonId) !== String(geometryId)) {
      this.resetData()
    }

    if (!isRefresh) {
      this.setLoading(true)
      this.pyramidLoading = true
    }
    this.error = null
    this.noData = false

    const { isZipCode = false, isAdminBoundary = false } = shapeOptions
    if (!isZipCode && !isAdminBoundary) {
      if (!cachedPolygons && !isRefresh) {
        this.startWorldpopPolling({ geoPolygonId: geometryId, scope: 'standalone' })
      }
    } else {
      runInAction(() => {
        this.worldpopAvailable = true
        this.worldpopLoading = false
      })
    }

    try {
      let polygons
      const targetGroup = dataStore.targetGroups.find(g => g.pk === this.targetGroupId)
      const shape = (targetGroup && targetGroup.regions.find(r => String(r.id || r.inner_id || r.remote_db_id) === String(geometryId))) ||
        (selectionStore.item && String(selectionStore.item.id || selectionStore.item.inner_id || selectionStore.item.remote_db_id) === String(geometryId) ? selectionStore.item : null)

      const cacheKey = `${dsName}_${JSON.stringify(shapeOptions)}`
      let useCache = false

      if (!isRefresh && !cachedPolygons) {
        if (shape && shape.sidebarCache && shape.sidebarCache.has(cacheKey)) {
          useCache = true
        }
      }

      const loadData = async (forceFetch = false) => {
        if (useCache && !forceFetch) {
          polygons = shape.sidebarCache.get(cacheKey)
          this.rawSinglePolygonData = polygons
        } else if (cachedPolygons && !forceFetch) {
          polygons = cachedPolygons
          this.rawSinglePolygonData = polygons
        } else {
          const config = { cancelToken: localCancelSource.token }
          if (shapeOptions && (shapeOptions.isAdminBoundary || shapeOptions.isZipCode)) {
            if (!config.headers) config.headers = {}
            config.headers['Ds-Name'] = 'landscan'
          }
          const response = await this.fetchPopulationDataResponseForShape(geometryId, shapeOptions, config)
          if (this.cancelSource !== localCancelSource) return false
          const data = response.data
          polygons = this.extractPopulationGeometryList(data)

          if (shapeOptions && (shapeOptions.isAdminBoundary || shapeOptions.isZipCode) && polygons.length > 0) {
            try {
              polygons[0].landscan_stats = polygons[0].stats
              const wpConfig = {
                cancelToken: localCancelSource.token,
                headers: { 'Ds-Name': 'worldpop' }
              }
              const wpResponse = await this.fetchPopulationDataResponseForShape(geometryId, shapeOptions, wpConfig)
              if (this.cancelSource === localCancelSource) {
                const wpPolygons = this.extractPopulationGeometryList(wpResponse.data)
                if (wpPolygons.length > 0) {
                  polygons[0].worldpop_stats = wpPolygons[0].stats
                  if (wpPolygons[0].population_datas) {
                    const wpData = wpPolygons[0].population_datas
                    if (!polygons[0].population_datas) polygons[0].population_datas = {}
                    Object.keys(wpData).forEach(year => {
                      if (!polygons[0].population_datas[year]) polygons[0].population_datas[year] = []
                      polygons[0].population_datas[year] = polygons[0].population_datas[year].concat(wpData[year])
                    })
                  }
                }
              }
            } catch (e) {
              console.warn('Failed to fetch worldpop data for boundary', e)
            }
          }
          this.rawSinglePolygonData = polygons
        }
        return true
      }

      try {
        const success = await loadData()
        if (success === false) return
      } catch (err) {
        if (useCache) {
          console.warn('Error loading single polygon from cache, refetching...', err)
          shape.sidebarCache.delete(cacheKey)
          useCache = false
          const success = await loadData(true)
          if (success === false) return
        } else {
          throw err
        }
      }

      if (polygons && polygons.length > 0) {
        if (!useCache && !cachedPolygons && !isRefresh) {
          if (shape && shape.sidebarCache) {
            shape.sidebarCache.set(cacheKey, polygons)
          }
        }
        this.noData = false

        if (polygons[0].worldpop_stats && polygons[0].landscan_stats) {
          polygons[0].stats = this.populationDataset === 'worldpop' ? polygons[0].worldpop_stats : polygons[0].landscan_stats
        }

        const polygon = this.getFilteredPolygon(polygons[0])

        // console.log('fetchDataForSinglePolygon polygon:', polygon)

        // Process the polygon data similar to fetchDataForAllPolygons
        // The polygon should have stats with total_population, hh_income, etc.
        const polygonKey = polygon.id != null ? polygon.id : geometryId
        this.setSinglePolygon(polygonKey, polygon, shapeOptions)
        this.targetGroupName = polygonName || polygon.name || 'Polygon'

        // Populate stats from population_datas if missing (e.g., Admin Boundaries)
        if (polygon.population_datas && (!polygon.stats || !polygon.stats.total_population)) {
          if (!polygon.stats) polygon.stats = {}
          polygon.stats.total_population = []
          polygon.stats.male_population = []
          polygon.stats.female_population = []
          const ds = this.populationDataset || 'landscan'
          Object.keys(polygon.population_datas).sort().forEach(yearStr => {
            const arr = polygon.population_datas[yearStr]
            if (Array.isArray(arr)) {
              const rec = arr.find(d => d.type === ds) || arr[0]
              if (rec) {
                const year = parseInt(yearStr, 10)
                polygon.stats.total_population.push({ year, value: rec.sum || rec.pop_tot || 0 })
                polygon.stats.male_population.push({ year, value: rec.male || rec.mal_tot || 0 })
                polygon.stats.female_population.push({ year, value: rec.female || rec.fem_tot || 0 })
              }
            }
          })
        }
        // Build years and latestYear from polygon stats for income/people tabs
        const yearSet = new Set()
        if (polygon.stats) {
          const yearSources = [
            polygon.stats.total_population,
            polygon.stats.hh_income,
            polygon.stats.male_population,
            polygon.stats.female_population
          ]
          yearSources.forEach(source => {
            if (Array.isArray(source)) {
              source.forEach(item => {
                if (item && item.year) yearSet.add(item.year)
              })
            }
          })
        }
        const sortedYears = Array.from(yearSet).sort()
        this.years = sortedYears.map(year => ({
          id: year,
          label: year.toString(),
          val: year
        }))
        this.updateLatestYear()
        this.incomeSelectedYear = '2021'
        this.populationSelectedYear = this.latestYear

        // Process population trends if available
        // Check if stats exists and has total_population array
        if (polygon.stats && polygon.stats.total_population && Array.isArray(polygon.stats.total_population)) {
          this.populationTrends = polygon.stats.total_population.map(item => ({
            year: item.year,
            population: item.value || 0,
            total_population: item.value || 0,
            average_population: item.value || 0 // For single polygon, average = total
          }))
          // console.log('fetchDataForSinglePolygon processed populationTrends:', this.populationTrends)
        } else {
          console.warn('fetchDataForSinglePolygon: polygon.stats.total_population not found or not an array', polygon.stats)
          this.populationTrends = []
        }

        // Process male/female data (latest)
        if (polygon.stats) {
          const latestMalePop = polygon.stats.male_population && polygon.stats.male_population.length > 0
            ? polygon.stats.male_population[polygon.stats.male_population.length - 1]
            : null
          const latestFemalePop = polygon.stats.female_population && polygon.stats.female_population.length > 0
            ? polygon.stats.female_population[polygon.stats.female_population.length - 1]
            : null

          if (latestMalePop || latestFemalePop) {
            this.maleFemaleData = {
              male: latestMalePop ? latestMalePop.value : 0,
              female: latestFemalePop ? latestFemalePop.value : 0
            }
          }

          // Get latest total population
          const latestPop = polygon.stats.total_population && polygon.stats.total_population.length > 0
            ? polygon.stats.total_population[polygon.stats.total_population.length - 1]
            : null
          const firstPop = polygon.stats.total_population && polygon.stats.total_population.length > 0
            ? polygon.stats.total_population[0]
            : null
          if (latestPop) {
            this.updateTotalPopulationForSelectedYear()
            this.growthRate = (latestPop.value - firstPop.value) / firstPop.value * 100
            this.growthRate = this.growthRate.toFixed(1)
            // this.updateGrowthRateForSelectedYear()
          }
        }

        // Calculate density for single polygon (latest year)
        const yearNum = this.getLatestYearNumber()
        // const area = polygon.stats && (polygon.stats.area_sqkm || polygon.stats.area_km)
        const regionAreaKm = this.getRegionAnalysisAreaKmForShape(geometryId, shapeOptions)

        if (yearNum && regionAreaKm) {
          const latestPop = polygon.stats.total_population && polygon.stats.total_population.length > 0
            ? polygon.stats.total_population[polygon.stats.total_population.length - 1].value
            : 0
          const ratio = regionAreaKm > 0 ? (latestPop / Number(regionAreaKm)) : null
          this.density = ratio !== null && Number.isFinite(ratio) ? Number(ratio.toFixed(3)) : null
        }

        // People tab size: Region Analysis analysis.area only (synced again in finally)
        // const regionAreaKm = this.getRegionAnalysisAreaKmForShape(geometryId, shapeOptions)
        if (regionAreaKm) {
          this.areaUnit = 'km²'
          this.applyPeopleTabSizeFromAreaKm(regionAreaKm)
        } else {
          this.areaKm = null
          this.areaMi = null
          this.size = null
        }
      } else {
        this.noData = true
        this.error = 'No polygon data available'
        // Clear stale chart data so previous polygon charts don't render
        this.populationData = null
        this.populationTrends = []
        this.polygonData = []
        this.maleFemaleData = null
        this.totalPopulation = null
        this.growthRate = null
        this.density = null
        this.size = null
        this.areaKm = null
        this.areaMi = null
        this.years = []
        this.latestYear = null
        this.incomeSelectedYear = null
        this.showPolygonLines = false
        this.showIndividualPolygonLines = false
        this.showIncomePolygonData = false
        this.selectedLine = null
        this.selectedLinePopulationDatas = null
        this.clearSinglePolygon()
      }
    } catch (error) {
      if (this.cancelSource === localCancelSource) {
        if (!axios.isCancel(error)) {
          console.error('Failed to fetch polygon data:', error)
        }
        this.error = error && error.message ? error.message : 'Failed to fetch polygon data'
      }
    } finally {
      if (this.cancelSource === localCancelSource) {
        this._fetchingTargetGroupId = null
        this._fetchingGeometryId = null
        this._fetchingDataset = null
        this.syncPeopleTabSizeFromRegionAnalysis()
        this.setLoading(false)
        this.pyramidLoading = false
      }
    }
  }

  // Generate a random hex color
  generateRandomHexColor () {
    return `#${Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0')}`
  }

  @action removePolygonFromChart (polygonId) {
    this.polygonData = this.polygonData.filter(p => p.id !== polygonId)

    // Recompute aggregate data after removal
    if (this.polygonData.length > 0) {
      this.computeAggregateDataFromPolygons()
    } else {
      // If no polygons left, hide polygon lines and clear data
      this.showPolygonLines = false
      this.showIndividualPolygonLines = false
      this.showIncomePolygonData = false
      this.populationTrends = []
      this.maleFemaleData = null
    }
  }

  // Pure data refresh. Safe to call any time polygon data changes underneath an existing selection
  @action refreshPolygonAggregatesFromRawData () {
    if (!this.polygonData || this.polygonData.length === 0) {
      // Clear population trends if no polygons selected
      this.populationTrends = []
      return
    }

    // Compute aggregate population trends and store them
    this.populationTrends = this.computeAggregateTrendsFromPolygons(this.polygonData)
    this.updateTotalPopulationForSelectedYear()
    const yearNum = this.getLatestYearNumber()
    const sumArea = this.polygonData.reduce((sum, polygon) => {
      return sum + (polygon.areaSqkm || 0)
    }, 0)
    if (this.aggSelector === 'avg') {
      const avgDensity = this.calculateAverageOfRatios(this.polygonData, yearNum)
      if (avgDensity !== null) this.density = avgDensity
    } else if (yearNum && sumArea > 0) {
      const latest = this.populationTrends[this.populationTrends.length - 1]
      const avgPopulation = latest ? (latest.average_population || 0) : 0
      const ratioAvg = this.calculateRatioOfAverages(avgPopulation, sumArea, this.polygonData.length)
      if (ratioAvg !== null) this.density = ratioAvg
    }

    // Compute aggregate male/female data (using latest year data from each polygon)
    const totalMale = this.polygonData.reduce((sum, polygon) => {
      return sum + (polygon.maleFemaleData ? (polygon.maleFemaleData.male || 0) : 0)
    }, 0)
    const totalFemale = this.polygonData.reduce((sum, polygon) => {
      return sum + (polygon.maleFemaleData ? (polygon.maleFemaleData.female || 0) : 0)
    }, 0)

    this.maleFemaleData = {
      male: totalMale,
      female: totalFemale
    }

    // This helps MobX detect the change
    this.populationTrends = [...this.populationTrends]
  }

  // computes agg data for custom selection of polygons
  @action computeAggregateDataFromPolygons () {
    this.refreshPolygonAggregatesFromRawData()

    // Clear single polygon data since we're in multi-select mode
    this.singlePolygonData = null
    this.singlePolygonId = null

    // Set target group name to indicate custom selection
    if (!this.originalTargetGroupName) {
      this.originalTargetGroupName = this.targetGroupName
    }
    this.targetGroupName = 'Custom Selection'

    // Show aggregate line initially (not individual lines)
    this.showPolygonLines = false
    this.showIndividualPolygonLines = false
  }

  computeAggregateTrendsFromPolygons (polygons) {
    // Get all unique years from all polygons
    const allYears = new Set()
    polygons.forEach(polygon => {
      if (polygon.trends) {
        polygon.trends.forEach(trend => {
          if (trend.year) allYears.add(trend.year)
        })
      }
    })

    const sortedYears = Array.from(allYears).sort()

    // Compute aggregate population trends
    return sortedYears.map(year => {
      let totalPopulation = 0

      polygons.forEach(polygon => {
        const trend = polygon.trends && polygon.trends.find(t => t.year === year)
        if (trend) {
          totalPopulation += trend.population || 0
        }
      })

      const avgPopulation = polygons.length > 0 ? (totalPopulation / polygons.length) : 0

      return {
        year,
        population: this.aggSelector === 'avg' ? avgPopulation : totalPopulation,
        total_population: totalPopulation,
        average_population: avgPopulation
      }
    })
  }

  @action async addPolygonToChart (geometryId, shapeOptions = {}) {
    try {
      const response = await this.fetchPopulationDataResponseForShape(geometryId, shapeOptions)
      const data = response.data

      const polygons = this.extractPopulationGeometryList(data)

      if (polygons && polygons.length > 0) {
        const polygon = polygons[0]
        const polygonColors = [
          '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
          '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1'
        ]

        // Check if polygon already exists in polygonData
        const polygonKey = polygon.id != null ? polygon.id : geometryId
        const existingIndex = this.polygonData.findIndex(p => String(p.id) === String(polygonKey))
        if (existingIndex >= 0) {
          // Polygon already added, don't add again
          return
        }

        // Get color - use existing polygon color or assign from palette
        const currentIndex = this.polygonData.length
        const color = polygon.color || polygonColors[currentIndex % polygonColors.length]

        // Process trends
        let trends = []
        if (polygon.stats && polygon.stats.total_population) {
          trends = polygon.stats.total_population.map(item => ({
            year: item.year,
            population: item.value || 0
          }))
        }

        // Process income data
        let incomeData = []
        if (polygon.stats && polygon.stats.hh_income) {
          incomeData = polygon.stats.hh_income.map(item => ({
            year: item.year,
            income: item.value || 0
          }))
        }

        // Process male/female data (latest)
        let maleFemaleData = null
        if (polygon.stats) {
          const latestMalePop = polygon.stats.male_population && polygon.stats.male_population.length > 0
            ? polygon.stats.male_population[polygon.stats.male_population.length - 1]
            : null
          const latestFemalePop = polygon.stats.female_population && polygon.stats.female_population.length > 0
            ? polygon.stats.female_population[polygon.stats.female_population.length - 1]
            : null

          if (latestMalePop || latestFemalePop) {
            maleFemaleData = {
              male: latestMalePop ? latestMalePop.value : 0,
              female: latestFemalePop ? latestFemalePop.value : 0
            }
          }
        }

        // Add polygon to polygonData array
        const polygonDataItem = {
          id: polygonKey,
          name: polygon.name || `Polygon ${polygon.id}`,
          population_datas: polygon.population_datas || null,
          trends,
          incomeData,
          malePopulationData: polygon.stats && polygon.stats.male_population ? polygon.stats.male_population : [],
          femalePopulationData: polygon.stats && polygon.stats.female_population ? polygon.stats.female_population : [],
          maleFemaleData,
          color,
          areaSqkm: polygon.stats && (polygon.stats.area_sqkm || polygon.stats.area_km) ? (polygon.stats.area_sqkm || polygon.stats.area_km) : null,
          areaSqmi: polygon.stats && (polygon.stats.area_sqmi || polygon.stats.area_mi) ? (polygon.stats.area_sqmi || polygon.stats.area_mi) : null
        }

        this.polygonData = [...this.polygonData, polygonDataItem]

        // Compute aggregate data from all selected polygons
        this.computeAggregateDataFromPolygons()

        // Show income polygon data
        this.showIncomePolygonData = true
      }
    } catch (error) {
      console.error('Failed to add polygon to chart:', error)
      this.error = error && error.message ? error.message : 'Failed to add polygon to chart'
    }
  }
}

export default RightSidebarStore
